import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
import { humanizeShippingMode } from '../utils/shippingMode';
import { ClockIcon, ArrowPathIcon, BoltIcon, EyeIcon, StarIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CancelConfirmedShipmentModal from '../components/Common/CancelConfirmedShipmentModal';
import PrintLabelsAction from '../components/Common/PrintLabelsAction';
// Task 86 (board confirm): reuse the SAME used-condition acknowledgement modal and the SAME
// server-response parser the planner's own confirm buttons use -- the board must go through the
// identical task-80 guard, not a second copy of it.
import { UsedConditionAckModal, usedConditionBlockFrom } from './ShipmentPlanner';
// Owner, 2026-09-02: the queue's hover popover renders the SAME FC map the placement workspace
// shows — one shared component, one pin builder, so the two surfaces can never drift.
import FcPinMap from '../components/Common/FcPinMap';
import { buildFcMapPins } from '../utils/placementBasket';

// ---------- Task 43 (live updates without refresh) ----------
// Owner: "on the previous runs - it should be updated without requiring a page refresh". Poll the
// runs list + watch list + action queue while the tab is visible; paused when hidden, cleared on
// unmount. Cadence adapts via GET /api/shipment-planner/background-busy (Task 42's deploy-guard
// endpoint): 10s while a background watch/probe cycle is running, 45s when idle, and a plain 20s
// when the endpoint can't be reached.
const POLL_MS_BUSY = 10000;
const POLL_MS_IDLE = 45000;
const POLL_MS_FALLBACK = 20000;

function useVisiblePolling(fn, delayMs) {
    useEffect(() => {
        if (!delayMs) return undefined;
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fn();
        }, delayMs);
        return () => clearInterval(id);
    }, [fn, delayMs]);
}

/**
 * Task 80 (backfill safety) — ACTIVE plans whose SKUs are NOT New-condition, keyed by run id.
 *
 * The New-condition invariant stops NEW plans from being created on used SKUs, but plans created
 * before it existed are still sitting ACTIVE and are one click from shipping (runs 167/170/173 and
 * the older watch draws, as of 2026-08-19). One cheap read gives both this page and the
 * Recommended board a "used SKU — do not confirm" badge so those are visible at a glance instead of
 * being discovered at the label printer.
 */
export function useUsedSkuAlerts(pollMs) {
    const [byRun, setByRun] = useState(() => new Map());
    const load = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/used-sku-alerts');
            const next = new Map();
            (res.data?.alerts || []).forEach(a => {
                const key = String(a.runId);
                if (!next.has(key)) next.set(key, []);
                next.get(key).push(a);
            });
            setByRun(next);
        } catch (err) {
            // Non-fatal: the badge is an extra safety net, never a reason to fail the page.
            console.error('Failed to load used-SKU alerts:', err);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    useVisiblePolling(load, pollMs);
    return byRun;
}

/**
 * Task 80 — true when task 81's single-product line already renders a used-SKU warning for this
 * row, so the task-80 audit badge stays out of the way. (Single-product rows show
 * `SkuConditionLine`, which turns red on its own; multi-product rows use `containsUsedSku`.)
 */
export function usedSkuAlreadyFlagged(row) {
    const products = row?.products || [];
    if (products.length !== 1) return false;
    const p = products[0];
    return !!p?.sku && p.itemCondition !== null && p.itemCondition !== undefined
        && p.itemCondition !== '' && p.newCondition === false;
}

/** Task 80 — the "used SKU — do not confirm" badge. Red, unmissable, with the offending FNSKUs. */
export function UsedSkuBadge({ alerts }) {
    if (!alerts || alerts.length === 0) return null;
    const detail = alerts
        .map(a => `${a.asin || ''} ${a.sku}${a.fnsku ? ' / ' + a.fnsku : ''} — ${a.conditionLabel}`)
        .join('\n');
    return (
        <span
            title={`This plan is on ${alerts.length} non-New SKU(s). Amazon prints the condition from the MSKU, so its item labels would say used.\n\n${detail}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800 border border-red-300 whitespace-nowrap"
        >
            <span aria-hidden="true">&#9888;</span> used SKU — do not confirm
        </span>
    );
}

// Subtle "updated Ns ago" caption -- re-renders itself on a slow tick so background refreshes
// stay visible without any table flicker.
function UpdatedAgo({ timestamp }) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 5000);
        return () => clearInterval(id);
    }, []);
    if (!timestamp) return null;
    const secs = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
    const label = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m`;
    return <span className="text-xs text-gray-400 whitespace-nowrap">updated {label} ago</span>;
}

// ---------- Task 25 (action queue) ----------
// Owner: "we should be accumulating several shipment workflows from Amazon, we need a way to be
// able to understand which of these, if any, should be acted on." Backed by
// GET /api/shipment-planner/action-queue (ShipmentPlannerAnalysisService#getActionQueue) -- the
// last 7 days of calibrate_placement_probe, deduped per plan_hash, filtered to plans whose best
// option is TAKE/CONSIDER or carries a "needed FC offered" signal.

// Task 78 (owner: "[put] the commentary into a tooltip"): `reason` renders as a native hover
// tooltip on the pill itself instead of a separate line of text -- a long decisionReason no
// longer widens its table column. The trailing "ⓘ" only appears when there's something to show.
function DecisionPill({ decision, reason }) {
    const title = reason || undefined;
    const suffix = reason ? <span className="ml-1 opacity-70">ⓘ</span> : null;
    const cls = reason ? 'cursor-help' : '';
    if (decision === 'TAKE') {
        return <span title={title} className={`px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-semibold ${cls}`}>TAKE{suffix}</span>;
    }
    if (decision === 'CONSIDER') {
        return <span title={title} className={`px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold ${cls}`}>CONSIDER{suffix}</span>;
    }
    if (decision === 'SKIP') {
        return <span title={title} className={`px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold ${cls}`}>SKIP{suffix}</span>;
    }
    if (reason) {
        // No decision verdict, but there IS commentary (e.g. cost-fallback pick) -- still show it.
        return <span title={title} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium cursor-help">notes{suffix}</span>;
    }
    return null;
}

function NeededFcPill({ hits }) {
    if (!hits || hits.length === 0) return null;
    return (
        <span
            className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold whitespace-nowrap"
            title={`Amazon offered a building we need: ${hits.join(', ')}`}
        >
            NEEDED FC OFFERED
        </span>
    );
}

// Task 29 (draw-quality advisor): how this row's draw ranked against the same plan's prior draws
// over the trailing 30 days (exact composition only). Absent when there's no prior draw to rank
// against -- the backend leaves drawPercentile null.
function DrawPercentileChip({ percentile, sampleSize }) {
    if (percentile === null || percentile === undefined) return null;
    const p = Math.round(percentile * 100);
    return (
        <span
            className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-semibold whitespace-nowrap"
            title={`This draw beats ${p}% of this plan's ${sampleSize} prior draw${sampleSize === 1 ? '' : 's'} over the trailing 30 days`}
        >
            p{p} draw
        </span>
    );
}

function CoverageGain({ label, pp }) {
    if (pp === null || pp === undefined || pp <= 0) return null;
    return (
        <span className="text-xs font-medium text-green-700">
            +{pp.toFixed(1)}pp {label}
        </span>
    );
}

function AsinQtyList({ asins }) {
    if (!asins || asins.length === 0) return <span className="text-gray-400">—</span>;
    return (
        <span className="text-gray-700">
            {asins.map((a, i) => (
                <span key={a.asin} className="font-mono">
                    {i > 0 && ', '}{a.asin} <span className="text-gray-400">×{a.qty}</span>
                </span>
            ))}
        </span>
    );
}

// Task 43: shipping-method chip (raw SP-API mode in the title, humanized label in the chip).
export function ShippingMethodChip({ method }) {
    if (!method) return <span className="text-gray-400 text-xs">—</span>;
    return (
        <span
            className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-medium whitespace-nowrap"
            title={method}
        >
            {humanizeShippingMode(method)}
        </span>
    );
}

// Task 43: product name(s) + ASIN(s) for a run row -- first product name (falls back to the ASIN
// when no title is known) plus "+N more" when multi, full list in the hover title.
function ProductsCell({ asins }) {
    if (!asins || asins.length === 0) return <span className="text-gray-400">—</span>;
    const first = asins[0];
    const fullList = asins
        .map(a => `${a.name ? a.name + ' — ' : ''}${a.asin}${a.qty != null ? ` ×${a.qty}` : ''}`)
        .join('\n');
    return (
        <div className="min-w-0 max-w-[18rem]" title={fullList}>
            <div className="text-gray-700 truncate">
                {first.name || first.asin}
                {asins.length > 1 && <span className="text-gray-400"> +{asins.length - 1} more</span>}
            </div>
            <div className="font-mono text-[11px] text-gray-400 truncate">
                {asins.map(a => a.asin).join(', ')}
            </div>
        </div>
    );
}

// ---------- Task 103: FC hover tooltips ----------
// Owner: "when you hover over the FCs on the shipment que the fc city name and state should
// appear".
//
// The dictionary is fetched ONCE per page load and shared by every chip. The Shipment Queue can put
// hundreds of FC codes on screen (many runs x several placement options x up to five FCs each), so
// a per-chip lookup is out of the question -- that is the shape of mistake that once turned a
// PO-items query into 43 seconds.
//
// Module-scoped promise, not component state: several components render FC codes (the queue's
// per-option chips, the Recommended board, its detail rows) and they must not each trigger a fetch.

let fcLocationsPromise = null;

export function resetFcLocationsCache() {   // test seam
    fcLocationsPromise = null;
}

export function loadFcLocations() {
    if (!fcLocationsPromise) {
        fcLocationsPromise = ApiClient.get('/api/shipment-planner/fc-locations')
            .then(res => res?.data || {})
            // Cosmetic data: a failure degrades every tooltip to "location unknown", never breaks
            // a render, and is not retried in a loop.
            .catch(() => ({}));
    }
    return fcLocationsPromise;
}

export function useFcLocations() {
    const [locations, setLocations] = useState({});
    useEffect(() => {
        let cancelled = false;
        loadFcLocations().then(d => { if (!cancelled) setLocations(d || {}); });
        return () => { cancelled = true; };
    }, []);
    return locations;
}

/**
 * "HEA2 — Chattanooga, Tennessee", or "HEA2 — location unknown".
 *
 * An unresolved FC says so explicitly. `report_amazon_fc_locations` was ~40% wrong until it began
 * self-healing from observed Amazon addresses on 2026-08-18, and 10 of the 93 FCs in recent runs
 * still do not resolve -- so this path is real. A confidently wrong city is worse than none: that
 * exact error once sent an operator looking for stock in the wrong state.
 */
export function fcTooltip(code, locations) {
    if (!code) return '';
    const loc = locations ? locations[String(code).toUpperCase()] : null;
    const parts = [loc?.city, loc?.state].filter(Boolean);
    return parts.length > 0 ? `${code} — ${parts.join(', ')}` : `${code} — location unknown`;
}

/** One FC code carrying its own hover text. Shared so the tooltip appears everywhere FC codes do. */
export function FcCode({ code, locations, className }) {
    return <span className={className} title={fcTooltip(code, locations)}>{code}</span>;
}

function DestinationFcs({ destinationFcs, neededFcHits }) {
    const locations = useFcLocations();
    if (!destinationFcs || destinationFcs.length === 0) return <span className="text-gray-400">—</span>;
    const needed = new Set(neededFcHits || []);
    return (
        <span className="font-mono text-xs">
            {destinationFcs.map((fc, i) => (
                <span key={fc}>
                    {i > 0 && ', '}
                    <FcCode
                        code={fc}
                        locations={locations}
                        className={`cursor-help ${needed.has(fc)
                            ? 'font-bold text-indigo-700 underline decoration-2' : 'text-gray-600'}`}
                    />
                </span>
            ))}
        </span>
    );
}

function ActionQueueCard({ item, onReanalyze }) {
    return (
        <div className="border border-gray-200 rounded-md p-4 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <DecisionPill decision={item.decision} />
                <NeededFcPill hits={item.neededFcHits} />
                <DrawPercentileChip percentile={item.drawPercentile} sampleSize={item.drawSampleSize} />
                <CoverageGain label="1-day" pp={item.covGain1dPp} />
                <CoverageGain label="2-day" pp={item.covGain2dPp} />
                <span className="ml-auto text-xs text-gray-400">
                    {item.staleness === 'today' ? 'Analyzed today' : `Analyzed ${item.staleness}`}
                    {item.source ? ` · ${item.source === 'PROBE' ? '6-hourly probe'
                        : item.source === 'WATCH' ? 'background watch' : 'manual analyze'}` : ''}
                </span>
            </div>

            <div className="text-sm mb-1">
                <AsinQtyList asins={item.asins} />
            </div>

            <div className="text-sm mb-2">
                <span className="text-gray-400">Destinations: </span>
                <DestinationFcs destinationFcs={item.destinationFcs} neededFcHits={item.neededFcHits} />
                <span className="text-gray-400 ml-3">Placement fee: </span>
                <span className="text-gray-700">${(item.placementFee || 0).toFixed(2)}</span>
            </div>

            {item.decisionReason && (
                <p className="text-sm text-gray-600 italic mb-2">{item.decisionReason}</p>
            )}

            {item.staleness && item.staleness !== 'today' && (
                <p className="text-xs text-amber-600 mb-2">
                    Options from {item.staleness} have likely expired — re-analyze to act.
                </p>
            )}

            <div className="flex justify-end">
                <button
                    onClick={() => onReanalyze(item)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    <BoltIcon className="h-3.5 w-3.5" />
                    Re-analyze now
                </button>
            </div>
        </div>
    );
}

function ActionQueueSection({ navigate, pollMs }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Owner 2026-08-21, verbatim: "the action que section is useless - we need to rethink this and
    // collapse it". Collapsed ALWAYS, never auto-expanded: it competed for attention with
    // Recommended, which answers the same question ("what should I act on") on the same
    // plan_hash composition AND can actually confirm. This section's only verb was "copy this ASIN
    // list to the clipboard and go redo the analyze by hand".
    const [expanded, setExpanded] = useState(false);
    const [toast, setToast] = useState(null);

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await ApiClient.get('/api/shipment-planner/action-queue');
            const data = res.data || [];
            setItems(data);
            setError(null);
        } catch (err) {
            console.error('Failed to load action queue:', err);
            if (!background) {
                setError(err.response?.data?.error || err.message || 'Failed to load action queue');
            }
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const backgroundLoad = useCallback(() => load({ background: true }), [load]);
    useVisiblePolling(backgroundLoad, pollMs);

    // Task 25 spec: prefer an existing prefill mechanism on /shipment-planner (?run= loads a
    // read-only historical run, not a fresh analyze; ?fromDraft=1 only imports the fixed
    // calibrate_war_room_po_draft table) -- neither accepts an arbitrary ad-hoc ASIN:qty list for a
    // NEW analyze. Falls back to copying the list to the clipboard + a toast, per spec, and opens
    // ASIN mode so the user can paste it in. Follow-up: wire a real prefill param into
    // ShipmentPlanner.js's AsinModePanel (out of scope here -- that file is owned by a parallel task).
    const handleReanalyze = async (item) => {
        const listText = (item.asins || []).map(a => `${a.asin}\t${a.qty}`).join('\n');
        try {
            await navigator.clipboard.writeText(listText);
            setToast('List copied — paste into the ASIN panel.');
        } catch (err) {
            console.error('Clipboard copy failed:', err);
            setToast('Could not copy the list — opening ASIN mode.');
        }
        setTimeout(() => setToast(null), 5000);
        navigate('/shipment-planner?mode=asin');
    };

    return (
        <div className="mb-6">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <BoltIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Action queue (superseded)</span>
                    {/* No count badge. It used to shout "N need a look" for the same compositions
                        Recommended was already showing, and the needed-FC signal that made some of
                        them worth a look now lives on the Recommended rows themselves. A number
                        here would only pull the operator back to the section that cannot act. */}
                    <span className="text-xs text-gray-400 truncate">
                        probe history — Recommended above is the actionable view
                    </span>
                </div>
                <span className="text-gray-400 text-sm">{expanded ? '▲ collapse' : '▼ expand'}</span>
            </button>

            {expanded && (
                <div className="mt-3">
                    {loading && <div className="text-gray-500 text-sm px-1">Loading action queue…</div>}
                    {error && <div className="text-red-600 text-sm px-1">Error: {error}</div>}
                    <div className="mb-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        <span className="font-semibold">Superseded by Recommended.</span> These are probe
                        draws from the last 7 days, keyed by composition — the same question the
                        Recommended board answers, except nothing here can be confirmed (the only
                        action was copying the ASIN list to re-analyze by hand). A draw that hit an FC
                        we need now shows as a badge on the Recommended row instead. Kept read-only
                        pending removal.
                    </div>
                    {!loading && !error && items.length === 0 && (
                        <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-6 text-center">
                            Nothing needs action — all recent workflows scored SKIP. The nightly probe keeps watching.
                        </div>
                    )}
                    {!loading && !error && items.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {items.map(item => (
                                <ActionQueueCard key={item.planHash} item={item} onReanalyze={handleReanalyze} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {toast && (
                <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2 rounded-md shadow-lg z-50">
                    {toast}
                </div>
            )}
        </div>
    );
}

// ---------- Task 76 (Recommended) ----------
// Owner: "on the shipment runs page - we need to add a section for 'Recommended' which shows the
// best of the options that have been run". Backed by GET /api/shipment-planner/recommended
// (RecommendedOptionsService): one row per shipment COMPOSITION (plan_hash over items_json), each
// carrying the best option seen across EVERY run of that composition in the last 72h -- not just
// the latest run. Rows are only confirmable while the draw is younger than the 36h validity
// window and the plan is a real, still-active, non-probe plan; everything else renders greyed and
// says "re-analyze to act" rather than pretending to be actionable.

export function money(v) {
    return (v === null || v === undefined) ? '—' : `$${Number(v).toFixed(2)}`;
}

export function ModeChip({ mode, carrier }) {
    if (!mode) return <span className="text-gray-400 text-xs">—</span>;
    const cls = mode === 'LTL' ? 'bg-orange-100 text-orange-800'
        : mode === 'MIXED' ? 'bg-yellow-100 text-yellow-800'
        : 'bg-sky-100 text-sky-800';
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}
              title={carrier || mode}>
            {mode}{carrier ? ` · ${carrier}` : ''}
        </span>
    );
}

// Task 78 (owner: "make it clear ... the box configuration"). Trims a decimal to at most one
// place ("49", "36.5") instead of the raw "49.00" a DB decimal column round-trips as JSON number.
function trimNum(n) {
    if (n === null || n === undefined) return null;
    const v = Number(n);
    if (!Number.isFinite(v)) return null;
    return Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, '');
}

// "1/box · 49×36.5×3 · 13 lb → 8 boxes" -- the exact shape the owner asked for. Falls back to
// whatever pieces are known (e.g. "1/box" alone) and returns null only when NOTHING is known,
// so the caller can render "packaging unknown" rather than an empty string.
export function boxConfigLabel(p) {
    if (!p || !p.unitsPerBox) return null;
    const parts = [`${p.unitsPerBox}/box`];
    const l = trimNum(p.boxLengthIn), w = trimNum(p.boxWidthIn), h = trimNum(p.boxHeightIn);
    if (l && w && h) parts.push(`${l}×${w}×${h}`);
    const wt = trimNum(p.boxWeightLbs);
    if (wt) parts.push(`${wt} lb`);
    const head = parts.join(' · ');
    return p.boxCount ? `${head} → ${p.boxCount} box${p.boxCount === 1 ? '' : 'es'}` : head;
}

// Task 78 owner addition ("also show our historical freight cost per asin"): this option's own
// freight/unit beside the 365-day per-ASIN benchmark, colored green when the quote beats history
// and red when it's worse -- gray whenever either side is unknown (never a guess dressed as fact).
export function HistoricalFreightBadge({ quotePerUnit, historicalPerUnit, source, dataThrough }) {
    if (historicalPerUnit === null || historicalPerUnit === undefined) {
        return <span className="text-gray-400">no freight history</span>;
    }
    const title = `365-day inbound freight benchmark${source ? ` — ${source}` : ''}`
        + (dataThrough ? ` (data through ${dataThrough})` : '');
    if (quotePerUnit === null || quotePerUnit === undefined) {
        return <span className="text-gray-400" title={title}>hist {money(historicalPerUnit)}/u</span>;
    }
    const beatsHistory = quotePerUnit <= historicalPerUnit;
    return (
        <span className={`font-medium ${beatsHistory ? 'text-emerald-700' : 'text-red-600'}`} title={title}>
            {money(quotePerUnit)}/u vs {money(historicalPerUnit)}/u hist
        </span>
    );
}

// Task 78 (owner: "show the item code from the price list"). Two-line product cell: name(s) on
// top, "ITEM-CODE · ASIN" (falls back to ASIN alone when the price list has no code) below --
// keeps the row to one visual identity line per product instead of a separate item-code column.
function ProductIdentifierLine({ products }) {
    if (!products || products.length === 0) return null;
    const shown = products.slice(0, 2);
    const remaining = products.length - shown.length;
    return (
        <div className="font-mono text-[11px] text-gray-400 truncate max-w-[20rem]">
            {shown.map((p, i) => (
                <span key={p.asin || i}>
                    {i > 0 && ', '}
                    {p.itemCode ? `${p.itemCode} · ` : ''}{p.asin}
                </span>
            ))}
            {remaining > 0 && <span> +{remaining} more</span>}
        </div>
    );
}

// Task 81 (owner, verbatim: "under the item code and asin - show the seller sku and FNSKU and
// item condition"). Compact "SKU ... · FNSKU ... · condition" line for one product, riding beneath
// the item code/ASIN line rather than adding a column. New-condition (or unresolved/unknown)
// renders gray/normal; any POSITIVELY-resolved non-New condition renders red with a warning icon
// and the owner's exact tooltip, so an operator scanning the board cannot miss it.
export function SkuConditionLine({ p }) {
    if (!p || !p.sku) return null;
    const fnsku = p.fnsku || '—';
    const hasCondition = p.itemCondition !== null && p.itemCondition !== undefined && p.itemCondition !== '';
    const condition = hasCondition ? p.itemCondition : 'unknown';
    const isUsed = hasCondition && p.newCondition === false;
    const text = `SKU ${p.sku} · FNSKU ${fnsku} · ${condition}`;
    if (isUsed) {
        return (
            <div
                className="flex items-center gap-1 font-mono text-[11px] text-red-600 font-semibold truncate max-w-[20rem]"
                title="used-condition SKU — do not confirm; rebuild this shipment on the New listing"
            >
                <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{text}</span>
            </div>
        );
    }
    return (
        <div className="font-mono text-[11px] text-gray-400 truncate max-w-[20rem]">
            {text}
        </div>
    );
}

// Task 81: multi-product summary-row chip -- per-product SKU/FNSKU/condition only shows in the
// expanded detail (compact-layout constraint), so the collapsed row needs its own signal when ANY
// item in the composition is on a used-condition SKU.
function UsedSkuChip() {
    return (
        <span
            className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-semibold whitespace-nowrap"
            title="One or more items in this composition are on a used-condition SKU — do not confirm; rebuild this shipment on the New listing"
        >
            <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" /> contains used SKU
        </span>
    );
}

// Task 78 (owner: "make it clear the number of units ... and the box configuration"). Single-
// product compositions show the box math inline; multi-product ones show a roll-up + a chevron
// that expands a per-product detail row (name, item code/ASIN, qty, box config, freight history).
function UnitsAndBoxesCell({ row, expanded, onToggleExpand }) {
    const products = row.products || [];
    const single = products.length === 1;
    return (
        <td className="px-3 py-2 whitespace-nowrap">
            <div className="text-sm text-gray-700 font-medium">
                {row.totalUnits} unit{row.totalUnits === 1 ? '' : 's'}
            </div>
            {single ? (
                <div className="text-[11px] text-gray-500">
                    {boxConfigLabel(products[0]) || 'packaging unknown'}
                </div>
            ) : (
                <button
                    onClick={onToggleExpand}
                    className="text-[11px] text-gray-500 hover:text-indigo-700 flex items-center gap-0.5"
                    title="Show per-product box configuration"
                >
                    {products.length} products · {row.totalBoxes != null
                        ? `${row.totalBoxes}${row.boxesFullyKnown ? '' : '+'} box${row.totalBoxes === 1 ? '' : 'es'}`
                        : 'boxes unknown'}
                    {expanded ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}
                </button>
            )}
        </td>
    );
}

// Task 78: per-product breakdown for a multi-ASIN composition, opened via the chevron in the
// Units/Boxes column. colSpan matches the 9-column Recommended table.
function RecommendedDetailRow({ row }) {
    return (
        <tr className="bg-gray-50">
            <td colSpan={9} className="px-6 py-2">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500">
                            <th className="text-left font-medium pr-3 pb-1">Product</th>
                            <th className="text-left font-medium pr-3 pb-1">Item code / ASIN</th>
                            <th className="text-right font-medium pr-3 pb-1">Qty</th>
                            <th className="text-left font-medium pr-3 pb-1">Box config</th>
                            <th className="text-left font-medium pb-1">Freight history</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(row.products || []).map((p, i) => (
                            <tr key={p.asin || i} className="border-t border-gray-200">
                                <td className="pr-3 py-1 text-gray-700 max-w-[16rem] truncate">{p.name || p.asin}</td>
                                <td className="pr-3 py-1 font-mono text-gray-500 max-w-[16rem]">
                                    <div className="truncate">{p.itemCode ? `${p.itemCode} · ` : ''}{p.asin}</div>
                                    <SkuConditionLine p={p} />
                                </td>
                                <td className="pr-3 py-1 text-right text-gray-700">{p.qty}</td>
                                <td className="pr-3 py-1 text-gray-500">{boxConfigLabel(p) || '—'}</td>
                                <td className="py-1 text-gray-500">
                                    {p.historicalFreightPerUnit !== null && p.historicalFreightPerUnit !== undefined
                                        ? <>{money(p.historicalFreightPerUnit)}/u <span className="text-gray-400">({p.historicalFreightSource})</span></>
                                        : <span className="text-gray-400">no history</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </td>
        </tr>
    );
}

// ---------- Task 86 (confirm directly from the board) ----------
// Owner: "the recommended section of the shipping plans no longer has a link to open and confirm
// the plan" -- Open turned out to be present (see the width-budget note above RecommendedSection);
// the real, pre-existing gap was that the board had NEVER had a confirm of its own -- every
// composition, however obviously TAKE, required a trip through Open -> the planner page to commit
// it. These helpers and the modal below let a confirmable row commit right from the board, through
// the SAME /api/shipment-planner/confirm endpoint the planner's own confirm buttons use (see
// RouteController#confirmShipmentPlan), with the SAME task-80 used-condition guard.
//
// LTL/own-carrier distinction (owner requirement): Amazon Partnered LTL needs a carrier Amazon
// itself quoted; when it quoted none, the plan's shipments carry no carrierName (RecommendedOptions
// Service#applyPlacementOption only ever sets `carrier` from the CHOSEN option's own shipments) and
// the operator must pick a USE_YOUR_OWN_CARRIER option per shipment via the planner's own picker
// (confirmOwnCarrier in ShipmentPlanner.js) -- there is no such picker on the board, and a blind
// POST /confirm against a carrier-less LTL option would either fail against Amazon or silently
// mis-book. So the board's Confirm stays disabled for exactly that shape (LTL/MIXED with no
// carrier), and says why.

/** True when this row/group-best is LTL (or a mixed placement) with no Amazon-assigned carrier --
 * the board cannot confirm it blind; the operator must open the planner to pick one. */
export function boardNeedsCarrierSelection(row) {
    if (!row) return false;
    return (row.mode === 'LTL' || row.mode === 'MIXED') && !row.carrier;
}

/** True when the board has everything a direct POST /api/shipment-planner/confirm needs: a real,
 * non-probe, currently-confirmable plan with both ids the endpoint requires. Carrier-gap LTL rows
 * pass this (they ARE confirmable plans) -- boardNeedsCarrierSelection is checked separately so the
 * button can render disabled-with-reason instead of vanishing outright. */
export function boardConfirmable(row) {
    if (!row) return false;
    if (!row.confirmable || row.probe) return false;
    return !!(row.planId && row.placementOptionId);
}

// Owner, 2026-09-01: which rows may join a batch confirm. Exactly the board's own gates -- a row
// the per-row Confirm button would refuse must never slip through in a batch either.
export function batchEligible(row) {
    return boardConfirmable(row) && !boardNeedsCarrierSelection(row);
}

// Owner correction, 2026-09-01: "the line by line confirmation ... was applied to the
// 'recommended' seection - we need it applied to the 'Shipment Queue'". Queue rows are raw run
// records (runs-search), so eligibility maps HONESTLY onto what a run row actually knows:
//  - ACTIVE only (a CONFIRMED/CANCELLED run has nothing left to confirm);
//  - never PROBE (probe plans are always voided at Amazon by design);
//  - never AWAITING_PALLETS (unpriced -- freight comes from measured pallets and the server
//    guard refuses an unquoted confirm; a checkbox would promise what the guard denies);
//  - both ids the confirm endpoint requires;
//  - still confirmable by AMAZON'S clock -- the row's confirmableUntil (Amazon's own
//    placementExpiration for the chosen option, ~72h observed) when the envelope carries it;
//    the 36h age window survives ONLY as the fallback for legacy rows without the field.
//    Owner, 2026-09-06: the 36h figure was an untested guess that discarded half the usable
//    plans (57 of 103 "expired" runs were still confirmable by Amazon's stored deadline).
// Priced LTL rows stay eligible: since the per-destination-carrier fix the confirm path selects
// each shipment's partnered carrier fresh and fails cleanly per item, and the board's
// carrier-less gate reads enrichment fields a queue row does not carry.
export const LEGACY_CONFIRMABILITY_FALLBACK_MS = 36 * 60 * 60 * 1000;

export function queueBatchEligible(run, nowMs = Date.now()) {
    if (!run) return false;
    if (String(run.status || '').toUpperCase() !== 'ACTIVE') return false;
    if (run.source_mode === 'PROBE') return false;
    if (run.planner_phase === 'AWAITING_PALLETS') return false;
    if (!run.inbound_plan_id) return false;
    if (!(run.selected_placement_id || run.recommended_placement_id)) return false;
    const until = Date.parse(String(run.confirmableUntil || ''));
    if (Number.isFinite(until)) return nowMs < until;
    const created = Date.parse(String(run.created_at || '').replace(' ', 'T'));
    if (!Number.isFinite(created) || nowMs - created > LEGACY_CONFIRMABILITY_FALLBACK_MS) return false;
    return true;
}

/** The confirm-batch selection a queue row stands for. Selected option wins over recommended --
 *  the same precedence the row's own FC/shipping-method cells already use. */
export function queueBatchSelection(run) {
    return {
        runId: Number(run.id),
        planId: run.inbound_plan_id,
        placementOptionId: run.selected_placement_id || run.recommended_placement_id,
    };
}

// Owner, 2026-09-01: "the products should be grouped by asin and qty, mix asin shipment should
// be grouped together". The group key IS the run's full composition -- sorted asin:qty pairs --
// so re-draws of the same thing sit together whatever order their items happened to be stored
// in, and identical mixed shipments cluster exactly the same way.
export function compositionKey(run) {
    const pairs = (run?.asins || [])
        .filter(a => a && a.asin)
        .map(a => `${a.asin}:${a.qty ?? '?'}`)
        .sort();
    // A run with no parseable items gets its own group rather than a shared "unknown" bucket --
    // two unreadable runs are not the same shipment.
    return pairs.length ? pairs.join('|') : `run-${run?.id}`;
}

// ---------- Owner, 2026-09-02: FC map on hover ("can a tooltip appear on the shipment queue
// page that shows this on hover?") ----------

/** Cache key for a row's stock/inbound picture: the DISTINCT sorted ASIN set. Quantities are
 *  deliberately excluded — stock and inbound are facts about the products, not about how many
 *  units this particular run ships — so re-draws and size variants share one fetch. */
export function queuePictureKey(run) {
    return [...new Set((run?.asins || []).map(a => a?.asin).filter(Boolean))].sort().join(',');
}

// One fetch per ASIN set for the page's life. The cache stores the in-flight promise, so two
// quick hovers can't race a duplicate request; a FAILED fetch evicts itself so a later hover
// retries instead of caching an error forever.
const placementPictureCache = new Map();
function fetchPlacementPicture(key) {
    if (!key) return Promise.resolve(null);
    if (!placementPictureCache.has(key)) {
        const asins = key.split(',');
        const url = asins.length > 1
            ? `/api/shipment-planner/placement-candidates?asins=${encodeURIComponent(key)}`
            : `/api/shipment-planner/placement-candidates/${encodeURIComponent(asins[0])}`;
        placementPictureCache.set(key, ApiClient.get(url)
            .then(r => ({
                currentFcUnits: r.data?.currentFcUnits || {},
                inboundFcUnits: r.data?.inboundFcUnits || {},
            }))
            .catch(() => { placementPictureCache.delete(key); return null; }));
    }
    return placementPictureCache.get(key);
}

/** The FC set this row would actually ship to: its picked option, else the recommendation,
 *  else the legacy flat list. */
export function rowPlanFcs(run) {
    const options = run?.placementFcs || [];
    const effective = options.find(o => o.selected) || options.find(o => o.recommended) || options[0];
    if (effective?.fcs?.length) return effective.fcs;
    return run?.destinationFcs || [];
}

/**
 * The hover popover: the row's proposed FCs render instantly (orange rings — they come from the
 * row itself); stock (blue) and inbound (violet) lazy-load once per ASIN set and fill in. Pinned
 * under the row's FC cell, sized like the workspace card, same honesty rule for unplottable FCs.
 */
function QueueFcMapPopover({ run }) {
    const locations = useFcLocations();
    const key = queuePictureKey(run);
    const [picture, setPicture] = useState(null);
    useEffect(() => {
        let on = true;
        fetchPlacementPicture(key).then(p => { if (on) setPicture(p); });
        return () => { on = false; };
    }, [key]);

    const { pins, unknown } = buildFcMapPins(
        locations, picture?.currentFcUnits, picture?.inboundFcUnits, rowPlanFcs(run));
    return (
        <div data-testid="queue-fc-map-popover"
             className="absolute left-0 top-full mt-1 z-40 w-[540px] max-w-[85vw] bg-white shadow-xl rounded-md border border-gray-300">
            <FcPinMap pins={pins} unknown={unknown} heightPx={240} selectedLabel="This plan"
                      note={picture ? null : 'loading stock/inbound…'} />
        </div>
    );
}

/** Groups in first-appearance order over the ALREADY-SORTED rows, so grouping clusters rows
 *  without fighting the operator's chosen sort. */
export function groupRunsByComposition(runs) {
    const byKey = new Map();
    for (const run of runs || []) {
        const key = compositionKey(run);
        if (!byKey.has(key)) byKey.set(key, { key, runs: [] });
        byKey.get(key).runs.push(run);
    }
    return Array.from(byKey.values());
}

/** The irreversibility dialog both batch surfaces show -- ONE text, so the promise made to the
 *  operator can never differ by section. */
export function batchConfirmPrompt(n) {
    const noun = n === 1 ? 'shipment plan' : 'shipment plans';
    return `Confirm ${n} ${noun} with Amazon?\n\n`
        + 'Each plan is confirmed one at a time through the same guarded path as the Confirm '
        + 'button, locking its placement and booking Amazon partnered transport. This cannot '
        + 'be undone. A plan that fails its guards is reported and skipped, never forced.';
}

/** POST + poll loop shared by the Recommended board and the Shipment Queue. Polls until the
 *  sequential worker finishes; a single failed poll is not a failed batch. */
export async function postAndPollBatch(selections, onOutcomes) {
    const res = await ApiClient.post('/api/shipment-planner/confirm-batch',
        { selections }, { timeout: 30000 });
    const batchId = res.data?.batchId;
    for (let i = 0; i < 240; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        try {
            const poll = await ApiClient.get(`/api/shipment-planner/confirm-batch/${batchId}`);
            onOutcomes(poll.data);
            if (poll.data?.status && poll.data.status !== 'RUNNING') break;
        } catch (pollErr) {
            // keep going
        }
    }
}

/** The batch bar + outcome report, shared verbatim by both surfaces. */
export function BatchConfirmBar({ count, running, outcomes, onStart, onClear, onDismiss }) {
    if (!(count > 0 || running || outcomes)) return null;
    return (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-3">
                <button
                    onClick={onStart}
                    disabled={running || count === 0}
                    className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {running ? 'Confirming one at a time…' : `Confirm ${count} selected…`}
                </button>
                {count > 0 && !running && (
                    <button onClick={onClear}
                            className="text-xs text-gray-600 hover:text-gray-800 underline">
                        clear selection
                    </button>
                )}
                {running && outcomes?.results && (
                    <span className="text-xs text-gray-600">
                        {outcomes.results.length} of {outcomes.requestedCount} processed
                    </span>
                )}
            </div>
            {outcomes?.status && outcomes.status !== 'RUNNING' && (
                <div className="mt-2 text-xs space-y-0.5">
                    {(outcomes.results || []).map(r => (
                        <div key={r.runId} className={r.outcome === 'CONFIRMED' ? 'text-emerald-800' : 'text-red-700'}>
                            Run #{r.runId} — {r.outcome}
                            {r.outcome === 'CONFIRMED' && r.shipmentCount != null && ` · ${r.shipmentCount} shipment(s)`}
                            {r.message ? `: ${r.message}` : ''}
                        </div>
                    ))}
                    <button onClick={onDismiss}
                            className="text-gray-500 underline mt-1">dismiss</button>
                </div>
            )}
        </div>
    );
}

// Task 86: the confirmation summary modal -- exactly what will be sent, before it's sent. Reuses
// row fields already on the /api/shipment-planner/recommended payload (task 78/81 enrichment), so
// no extra fetch is needed to populate it.
function ConfirmSummaryTable({ products }) {
    if (!products || products.length === 0) {
        return <div className="text-xs text-gray-400">No product detail on this row.</div>;
    }
    return (
        <table className="w-full text-xs border border-gray-200 rounded">
            <thead className="bg-gray-50">
                <tr>
                    <th className="text-left font-medium px-2 py-1">Product</th>
                    <th className="text-left font-medium px-2 py-1">Item code / ASIN</th>
                    <th className="text-left font-medium px-2 py-1">SKU / FNSKU</th>
                    <th className="text-left font-medium px-2 py-1">Condition</th>
                    <th className="text-right font-medium px-2 py-1">Qty</th>
                </tr>
            </thead>
            <tbody>
                {products.map((p, i) => (
                    <tr key={p.asin || i} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-gray-700 max-w-[12rem] truncate">{p.name || p.asin}</td>
                        <td className="px-2 py-1 font-mono text-gray-500">
                            {p.itemCode ? `${p.itemCode} · ` : ''}{p.asin}
                        </td>
                        <td className="px-2 py-1 font-mono text-gray-500">{p.sku || '—'} / {p.fnsku || '—'}</td>
                        <td className={`px-2 py-1 ${p.newCondition === false ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                            {p.itemCondition || 'unknown'}
                        </td>
                        <td className="px-2 py-1 text-right text-gray-700">{p.qty}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * Task 86 -- the board's confirmation dialog: exactly what will be confirmed (products, units,
 * destinations, carrier/freight/fee/per-unit, decision + reason, plan/run ids) before the operator
 * commits. `row` is a RecommendedOption (single row) or a group's `best` -- same shape either way.
 */
export function RecommendedConfirmModal({ open, row, onCancel, onConfirm, busy }) {
    if (!open || !row) return null;
    const needsCarrier = boardNeedsCarrierSelection(row);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-testid="board-confirm-modal">
                <div className="px-5 py-4 border-b">
                    <h3 className="text-base font-semibold text-gray-900">Confirm this shipment plan?</h3>
                    <p className="text-xs text-gray-500 mt-1 font-mono">
                        Run #{row.runId} · Plan {row.planId || '—'}
                    </p>
                </div>
                <div className="px-5 py-4 space-y-3 text-sm text-gray-700 max-h-[70vh] overflow-y-auto">
                    <ConfirmSummaryTable products={row.products} />
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                        <div><span className="text-gray-400">Units: </span>{row.totalUnits}</div>
                        <div>
                            <span className="text-gray-400">Destinations: </span>
                            {(row.destinationFcs || []).join(', ') || '—'}
                        </div>
                        <div>
                            <span className="text-gray-400">Mode / carrier: </span>
                            {row.mode || '—'}{row.carrier ? ` · ${row.carrier}` : ''}
                        </div>
                        <div>
                            <span className="text-gray-400">Freight: </span>
                            {row.freightKnown === false ? 'not quoted' : money(row.freight)}
                        </div>
                        <div><span className="text-gray-400">Placement fee: </span>{money(row.placementFee)}</div>
                        <div><span className="text-gray-400">Per unit: </span>{money(row.perUnitCost)}</div>
                        <div className="col-span-2">
                            <span className="text-gray-400">Decision: </span>{row.decision || '—'}
                        </div>
                    </div>
                    {row.decisionReason && (
                        <p className="text-xs italic text-gray-600">{row.decisionReason}</p>
                    )}
                    {needsCarrier && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                            This plan needs a carrier choice — Amazon quoted no partnered carrier for it.
                            Open the run to pick one; the board cannot confirm it blind.
                        </p>
                    )}
                </div>
                <div className="px-5 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
                    <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy || needsCarrier}
                        className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? 'Confirming…' : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RecommendedRow({ row, onOpen, onCancel, onConfirm, cancelling, expanded, onToggleExpand,
                          usedSkuAlerts, batchSelected, onToggleBatch }) {
    const dimmed = !row.confirmable;
    return (
        <>
        <tr className={`group ${dimmed ? 'bg-gray-50 text-gray-400' : 'hover:bg-emerald-50'}`}>
            <td className="px-3 py-2">
                {/* Owner, 2026-09-01: batch-confirm selection. Only rows the per-row Confirm
                    button would accept get a checkbox -- a batch must never widen the gate. */}
                {batchEligible(row) && onToggleBatch && (
                    <input
                        type="checkbox"
                        aria-label={`select for batch confirm: run ${row.runId}`}
                        checked={!!batchSelected}
                        onChange={() => onToggleBatch(row)}
                        className="mr-2 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 align-middle"
                    />
                )}
                <div className={`text-sm ${dimmed ? '' : 'text-gray-800 font-medium'} max-w-[20rem] truncate`}
                     title={(row.products || []).map(p => `${p.itemCode ? p.itemCode + ' · ' : ''}${p.name || p.asin} ×${p.qty}`).join('\n')}>
                    {row.compositionLabel || '—'}
                </div>
                <ProductIdentifierLine products={row.products} />
                {/* Task 81: seller SKU / FNSKU / item condition -- single-product compositions show
                    the full line inline; multi-product ones show it per-product in the expanded
                    detail instead (compact-layout constraint) and get a chip here when any item
                    resolved to a used condition. */}
                {(row.products || []).length === 1
                    ? <SkuConditionLine p={row.products[0]} />
                    : row.containsUsedSku && <div><UsedSkuChip /></div>}
                {/* Task 80 (backfill safety) — a BACKSTOP for task 81's per-product signal above,
                    not a second copy of it. Task 81 flags used SKUs from RecommendedOptionsService's
                    own per-product enrichment; this badge comes from the independent ACTIVE-run
                    audit over the run's stored items_json, so it still fires on rows whose product
                    enrichment came back empty. Suppressed once task 81 has already warned, so a row
                    never shows two red warnings for the same fact. */}
                {!row.containsUsedSku && !usedSkuAlreadyFlagged(row) && usedSkuAlerts && usedSkuAlerts.length > 0 && (
                    <div className="mt-1"><UsedSkuBadge alerts={usedSkuAlerts} /></div>
                )}
                {row.runsConsidered > 1 && (
                    <div className="text-[11px] text-gray-400">best of {row.runsConsidered} runs</div>
                )}
            </td>
            <td className="px-3 py-2">
                {/* Task 86: capped to 13rem so the chip row wraps onto a second line instead of
                    growing the column -- with drawPercentile + both coverage-gain chips present
                    this was one of the widest cells in the table. */}
                <div className="flex flex-wrap items-center gap-1.5 max-w-[13rem]">
                    <DecisionPill decision={row.decision} reason={row.decisionReason} />
                    {row.scoreKnown && row.score !== null && row.score !== undefined && (
                        <span className="text-xs font-semibold text-gray-700">{Number(row.score).toFixed(1)}</span>
                    )}
                    {/* Owner, 2026-09-01 (take guidance): an unpriced LTL row must say what it IS
                        -- a Phase-1 plan waiting on pallet measurements -- never imply a cost was
                        compared. "lowest cost" only renders when a cost actually existed. */}
                    {row.freightKnown === false ? (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium"
                              title="Phase-1 LTL plan: FC split and placement fee are known; freight and the verdict arrive when you measure the pallets">
                            unpriced — measure pallets to score
                        </span>
                    ) : !row.scoreKnown && (
                        <span className="text-xs text-gray-500 italic" title="No score was recorded for this run — picked on lowest freight + placement cost">
                            lowest cost
                        </span>
                    )}
                    <DrawPercentileChip percentile={row.drawPercentile} sampleSize={row.drawSampleSize} />
                    {/* The Action Queue's one genuinely unique signal, rehomed 2026-08-21: a draw
                        that landed on an FC we actually NEED, as opposed to one Amazon merely
                        offered. It belongs on a confirmable row. */}
                    <NeededFcPill hits={row.neededFcHits} />
                    <CoverageGain label="1-day" pp={row.covGain1dPp} />
                    <CoverageGain label="2-day" pp={row.covGain2dPp} />
                </div>
            </td>
            <td className="px-3 py-2">
                {/* Task 86: bounded + truncated (full list in the tooltip) -- an unbounded
                    comma-separated FC list was one of the columns forcing the table wider than the
                    page, pushing Actions off-screen behind a horizontal scroll. */}
                <div className="max-w-[9rem] truncate" title={(row.destinationFcs || []).join(', ')}>
                    {/* 2026-08-21: this was hard-coded to [] -- the component supported the
                        signal, but the only producer was the Action Queue, where it could not be
                        acted on. Now it rides the row that can actually be confirmed. */}
                    <DestinationFcs destinationFcs={row.destinationFcs} neededFcHits={row.neededFcHits} />
                </div>
            </td>
            <td className="px-3 py-2 text-right">
                {/* Freight Amazon never quoted stays "—": a placement fee alone is not a total. */}
                <div className="text-sm whitespace-nowrap">{money(row.totalCost)}</div>
                {/* Task 86: allowed to wrap (was whitespace-nowrap) and capped to 9rem -- this line
                    ("$X freight + $Y fee · $Z/unit") was one of the widest in the row. */}
                <div className="text-[11px] text-gray-400 max-w-[9rem] ml-auto">
                    {row.freightKnown === false
                        ? <span className="text-amber-600">freight not quoted</span>
                        : <>{money(row.freight)} freight</>}
                    {' + '}{money(row.placementFee)} fee
                    {row.perUnitCost !== null && row.perUnitCost !== undefined && ` · ${money(row.perUnitCost)}/unit`}
                </div>
                <div className="text-[11px] mt-0.5">
                    <HistoricalFreightBadge
                        quotePerUnit={row.freightPerUnit}
                        historicalPerUnit={row.historicalFreightPerUnit}
                        source={row.historicalFreightSource}
                        dataThrough={row.historicalFreightDataThrough}
                    />
                </div>
            </td>
            <td className="px-3 py-2"><ModeChip mode={row.mode} carrier={row.carrier} /></td>
            <UnitsAndBoxesCell row={row} expanded={expanded} onToggleExpand={onToggleExpand} />
            <td className="px-3 py-2 whitespace-nowrap">
                {/* Task 86: sourceMode moved into the tooltip -- age + expiry are the two facts an
                    operator scanning the board needs; the source mode is a click/hover away. */}
                <div className="text-xs text-gray-600" title={row.sourceMode || ''}>
                    {row.ageLabel || '—'}
                </div>
                <div className={`text-xs ${row.expired ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                    {row.expiryLabel || ''}
                </div>
            </td>
            <td className="px-3 py-2">
                {row.confirmable ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold whitespace-nowrap">
                        confirmable
                    </span>
                ) : (
                    <span className="text-xs text-amber-700 max-w-[9rem] inline-block truncate" title={row.notActionableReason || 're-analyze to act'}>
                        {row.notActionableReason || 're-analyze to act'}
                    </span>
                )}
            </td>
            {/* Task 86 (owner: "adjust the page format so no scroll is needed"): sticky to the
                right edge of the table's own overflow-x-auto scroller -- Open / Confirm / Cancel
                stay visible even if the row's other columns still need to scroll on a narrower
                screen than this board was designed for (see the width-budget note above
                RecommendedSection). group-hover keeps the sticky cell's background in step with
                the row's own hover highlight instead of floating as a mismatched rectangle. */}
            <td className={`sticky right-0 px-3 py-2 border-l border-gray-100 ${dimmed ? 'bg-gray-50 group-hover:bg-gray-50' : 'bg-white group-hover:bg-emerald-50'}`}>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => onOpen(row.runId)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                        Open
                    </button>
                    {boardConfirmable(row) && (
                        boardNeedsCarrierSelection(row) ? (
                            <span
                                className="text-xs text-gray-400 cursor-not-allowed"
                                title="This plan needs a carrier choice — open to choose a carrier"
                            >
                                Confirm
                            </span>
                        ) : (
                            <button
                                onClick={() => onConfirm(row)}
                                className="text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                            >
                                Confirm
                            </button>
                        )
                    )}
                    {row.planId && !row.probe && (
                        <button
                            onClick={() => onCancel(row)}
                            disabled={cancelling === row.planId}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                        >
                            {cancelling === row.planId ? 'Cancelling…' : 'Cancel plan'}
                        </button>
                    )}
                </div>
            </td>
        </tr>
        {expanded && (row.products || []).length > 1 && <RecommendedDetailRow row={row} />}
        </>
    );
}

// ---------- Task 82 (group by seller SKU) ----------
// Owner (verbatim): "group the items with the same seller sku together on the recommended
// section". The composition-level dedup in RecommendedOptionsService already collapses identical
// items_json into one row, but the SAME product still shows up in many DIFFERENT rows -- one per
// quantity/composition it was drawn under (e.g. B01M0TOP10 at runs 106, 148, 180). The seller SKU
// is the true identity here (task 80/81: the same ASIN can carry several SKUs, and the SKU decides
// the item's condition) -- so single-product rows sharing a SKU collapse into one group, headlined
// by the group's best option, with its sibling options available on expand.
//
// Multi-product compositions have no single SKU to group by. Grouping them by their SKU *set*
// would need a second identity scheme (and still collide arbitrarily as compositions vary) -- the
// simpler, honest choice is to leave them as their existing per-composition rows in a separate
// "Multi-product shipments" section below the SKU groups.

/**
 * Rows arrive from GET /api/shipment-planner/recommended already sorted best-first (server
 * ROW_ORDER: confirmable, then decision rank, then score desc, then newest) -- Array#filter/push
 * below preserves that relative order within each bucket, so `group.rows[0]` is always the best
 * option for that SKU without any extra client-side sort, and each SKU-group's insertion position
 * (first time that SKU is seen) naturally reflects the best rank among its options.
 */
export function groupRecommendedRows(rows) {
    const singleGroups = new Map(); // key -> { key, sku, rows: [] }
    const multi = [];
    for (const row of (rows || [])) {
        const products = row.products || [];
        if (products.length === 1 && products[0]) {
            const sku = products[0].sku || null;
            // No resolvable SKU: fall back to the row's own composition identity so it renders as
            // its own singleton group instead of being merged with other SKU-less rows.
            const key = sku ? `sku:${sku}` : `nosku:${row.planHash}`;
            if (!singleGroups.has(key)) singleGroups.set(key, { key, sku, rows: [] });
            singleGroups.get(key).rows.push(row);
        } else {
            multi.push(row);
        }
    }
    const groups = Array.from(singleGroups.values()).map(g => ({
        ...g,
        best: g.rows[0],
        optionsCount: g.rows.length,
        runsCount: g.rows.reduce((sum, r) => sum + (r.runsConsidered || 1), 0),
    }));
    return { groups, multi };
}

// Task 82: the group's sibling options, best-first (see groupRecommendedRows). Deliberately
// compact -- qty/run/destinations/decision/freight/age are what an operator needs to pick a
// DIFFERENT option than the headline one; anything else is a click away via Open.
function RecommendedGroupOptionsDetail({ group, onOpen }) {
    return (
        <tr className="bg-gray-50">
            <td colSpan={9} className="px-6 py-2">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500">
                            <th className="text-right font-medium pr-3 pb-1">Qty</th>
                            <th className="text-left font-medium pr-3 pb-1">Run</th>
                            <th className="text-left font-medium pr-3 pb-1">Destinations</th>
                            <th className="text-left font-medium pr-3 pb-1">Decision</th>
                            <th className="text-right font-medium pr-3 pb-1">Total</th>
                            <th className="text-left font-medium pr-3 pb-1">Drawn</th>
                            <th className="text-left font-medium pb-1">Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {group.rows.map(r => (
                            <tr key={r.planHash} className="border-t border-gray-200">
                                <td className="pr-3 py-1 text-right text-gray-700">{r.totalUnits}</td>
                                <td className="pr-3 py-1 text-gray-500">#{r.runId}</td>
                                <td className="pr-3 py-1"><DestinationFcs destinationFcs={r.destinationFcs} neededFcHits={r.neededFcHits} /></td>
                                <td className="pr-3 py-1"><DecisionPill decision={r.decision} reason={r.decisionReason} /></td>
                                <td className="pr-3 py-1 text-right text-gray-700">
                                    {r.freightKnown === false
                                        ? <span className="text-amber-600">not quoted</span>
                                        : money(r.totalCost)}
                                </td>
                                <td className="pr-3 py-1 text-gray-500 whitespace-nowrap">
                                    {r.ageLabel || '—'}
                                    <span className={r.expired ? 'text-amber-600 font-semibold ml-1' : 'text-gray-400 ml-1'}>
                                        {r.expiryLabel || ''}
                                    </span>
                                </td>
                                <td className="py-1">
                                    <button
                                        onClick={() => onOpen(r.runId)}
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                    >
                                        Open
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </td>
        </tr>
    );
}

// Task 82: header row for a SKU that has more than one option drawn for it. A single-option SKU
// (or a composition with no resolvable SKU) renders through the unchanged RecommendedRow instead
// -- so today's single-option display never regresses. group.rows arrive best-first (see
// groupRecommendedRows), so group.best doubles as the "best option" figures below.
function RecommendedGroupRow({ group, onOpen, onCancel, onConfirm, cancelling, expanded, onToggleExpand,
                               usedSkuAlerts, batchSelected, onToggleBatch, onSelectSuggested }) {
    const best = group.best;
    const product = (best.products || [])[0];
    const dimmed = !best.confirmable;
    const anyUsedSku = group.rows.some(r => r.containsUsedSku);
    const alreadyFlagged = product ? usedSkuAlreadyFlagged({ products: [product] }) : false;
    return (
        <>
        <tr className={`group ${dimmed ? 'bg-gray-50 text-gray-400' : 'hover:bg-emerald-50'}`}>
            <td className="px-3 py-2">
                {batchEligible(best) && onToggleBatch && (
                    <input
                        type="checkbox"
                        aria-label={`select for batch confirm: run ${best.runId}`}
                        checked={!!batchSelected}
                        onChange={() => onToggleBatch(best)}
                        className="mr-2 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 align-middle"
                    />
                )}
                {/* Owner, 2026-09-01: "which of these should I actually take?" -- the workspace's
                    set-cover suggestion, one click, ticking exactly those checkboxes. */}
                {product?.asin && onSelectSuggested && group.rows.length > 1 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onSelectSuggested(product.asin, group); }}
                        className="mr-2 text-[11px] text-indigo-600 hover:text-indigo-800 underline align-middle"
                        title="Pick the suggested combination of this product's open plans (marginal set-cover, the placement workspace's math) and tick those rows"
                    >
                        Select suggested
                    </button>
                )}
                <button onClick={onToggleExpand} className="text-left w-full" title="Show individual options for this SKU">
                    <div className={`text-sm ${dimmed ? '' : 'text-gray-800 font-medium'} max-w-[20rem] truncate`}>
                        {product?.name || best.compositionLabel || '—'}
                    </div>
                    <ProductIdentifierLine products={product ? [product] : []} />
                    <SkuConditionLine p={product} />
                    {anyUsedSku && !alreadyFlagged && <div><UsedSkuChip /></div>}
                    {!anyUsedSku && usedSkuAlerts && usedSkuAlerts.length > 0 && (
                        <div className="mt-1"><UsedSkuBadge alerts={usedSkuAlerts} /></div>
                    )}
                    <div className="text-[11px] text-gray-500 flex items-center gap-0.5">
                        {group.optionsCount} option{group.optionsCount === 1 ? '' : 's'} across {group.runsCount} run{group.runsCount === 1 ? '' : 's'}
                        {expanded ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}
                    </div>
                </button>
            </td>
            <td className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-1.5 max-w-[13rem]">
                    <DecisionPill decision={best.decision} reason={best.decisionReason} />
                    {best.scoreKnown && best.score !== null && best.score !== undefined && (
                        <span className="text-xs font-semibold text-gray-700">{Number(best.score).toFixed(1)}</span>
                    )}
                    {!best.scoreKnown && (
                        <span className="text-xs text-gray-500 italic" title="No score was recorded for this run — picked on lowest freight + placement cost">
                            lowest cost
                        </span>
                    )}
                    <DrawPercentileChip percentile={best.drawPercentile} sampleSize={best.drawSampleSize} />
                    <CoverageGain label="1-day" pp={best.covGain1dPp} />
                    <CoverageGain label="2-day" pp={best.covGain2dPp} />
                </div>
            </td>
            <td className="px-3 py-2">
                <div className="max-w-[9rem] truncate" title={(best.destinationFcs || []).join(', ')}>
                    <DestinationFcs destinationFcs={best.destinationFcs} neededFcHits={best.neededFcHits} />
                </div>
            </td>
            <td className="px-3 py-2 text-right">
                <div className="text-sm whitespace-nowrap">{money(best.totalCost)}</div>
                <div className="text-[11px] text-gray-400 max-w-[9rem] ml-auto">
                    {best.freightKnown === false
                        ? <span className="text-amber-600">freight not quoted</span>
                        : <>{money(best.freight)} freight</>}
                    {' + '}{money(best.placementFee)} fee
                    {best.perUnitCost !== null && best.perUnitCost !== undefined && ` · ${money(best.perUnitCost)}/unit`}
                </div>
                <div className="text-[11px] mt-0.5">
                    <HistoricalFreightBadge
                        quotePerUnit={best.freightPerUnit}
                        historicalPerUnit={best.historicalFreightPerUnit}
                        source={best.historicalFreightSource}
                        dataThrough={best.historicalFreightDataThrough}
                    />
                </div>
            </td>
            <td className="px-3 py-2"><ModeChip mode={best.mode} carrier={best.carrier} /></td>
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="text-sm text-gray-700 font-medium">
                    {best.totalUnits} unit{best.totalUnits === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] text-gray-500">
                    {boxConfigLabel(product) || 'packaging unknown'}
                </div>
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="text-xs text-gray-600" title={best.sourceMode || ''}>
                    {best.ageLabel || '—'}
                </div>
                <div className={`text-xs ${best.expired ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                    {best.expiryLabel || ''}
                </div>
            </td>
            <td className="px-3 py-2">
                {best.confirmable ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold whitespace-nowrap">
                        confirmable
                    </span>
                ) : (
                    <span className="text-xs text-amber-700 max-w-[9rem] inline-block truncate" title={best.notActionableReason || 're-analyze to act'}>
                        {best.notActionableReason || 're-analyze to act'}
                    </span>
                )}
            </td>
            <td className={`sticky right-0 px-3 py-2 border-l border-gray-100 ${dimmed ? 'bg-gray-50 group-hover:bg-gray-50' : 'bg-white group-hover:bg-emerald-50'}`}>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => onOpen(best.runId)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                        Open
                    </button>
                    {boardConfirmable(best) && (
                        boardNeedsCarrierSelection(best) ? (
                            <span
                                className="text-xs text-gray-400 cursor-not-allowed"
                                title="This plan needs a carrier choice — open to choose a carrier"
                            >
                                Confirm
                            </span>
                        ) : (
                            <button
                                onClick={() => onConfirm(best)}
                                className="text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                            >
                                Confirm
                            </button>
                        )
                    )}
                    {best.planId && !best.probe && (
                        <button
                            onClick={() => onCancel(best)}
                            disabled={cancelling === best.planId}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                        >
                            {cancelling === best.planId ? 'Cancelling…' : 'Cancel plan'}
                        </button>
                    )}
                </div>
            </td>
        </tr>
        {expanded && <RecommendedGroupOptionsDetail group={group} onOpen={onOpen} />}
        </>
    );
}

export function RecommendedSection({ navigate, pollMs, onRunsChanged, onCountChange }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [cancelling, setCancelling] = useState(null);
    // Task 78: which rows' per-product box-configuration detail is open (multi-ASIN compositions only).
    const [expandedRows, setExpandedRows] = useState(() => new Set());
    // Task 82: which SKU groups' sibling-options detail is open.
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    // Task 80: which of these recommended (ACTIVE) plans are sitting on used-condition SKUs.
    const usedSkuAlerts = useUsedSkuAlerts(pollMs);
    // 2026-09-03 collapse work: the wrapper's count chip — collapsed must never mean invisible.
    useEffect(() => { if (onCountChange) onCountChange(rows.length); }, [rows, onCountChange]);
    const toggleRowExpand = useCallback((planHash) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(planHash)) next.delete(planHash); else next.add(planHash);
            return next;
        });
    }, []);
    const toggleGroupExpand = useCallback((key) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);
    // Task 82: group single-product rows by seller SKU; multi-product compositions keep their
    // existing per-composition rows (see groupRecommendedRows).
    const { groups, multi } = useMemo(() => groupRecommendedRows(rows), [rows]);

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await ApiClient.get('/api/shipment-planner/recommended');
            setRows(res.data || []);
            setError(null);
        } catch (err) {
            console.error('Failed to load recommended options:', err);
            if (!background) {
                setError(err.response?.data?.error || err.message || 'Failed to load recommended options');
            }
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const backgroundLoad = useCallback(() => load({ background: true }), [load]);
    useVisiblePolling(backgroundLoad, pollMs);

    const openRun = (runId) => navigate(`/shipment-planner?run=${runId}`);

    const cancelPlan = async (row) => {
        if (!row.planId) return;
        if (!window.confirm(`Cancel this draft plan (Run #${row.runId}) on Amazon?`)) return;
        setCancelling(row.planId);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${row.planId}`);
            await load();
            if (onRunsChanged) onRunsChanged();
        } catch (err) {
            console.error('Cancel failed:', err);
            alert('Cancel failed: ' + (err.response?.data?.error || err.message));
        }
        setCancelling(null);
    };

    // Task 86: confirm directly from the board. `confirmTarget` holds the row (or group's `best`)
    // the summary modal is open for; `usedAckItems`/`usedAckRetry` mirror the exact task-80 pattern
    // ShipmentPlanner.js's own confirm buttons use (confirmPlan/confirmSelected/confirmOwnCarrier)
    // -- server refuses with usedConditionBlock, the operator sees the offending SKUs and ticks an
    // explicit acknowledgement, and ONLY THEN is the same confirm retried with
    // acknowledgeUsedCondition:true. No guard is bypassed; every attempt (and every acknowledged
    // override) goes through the same server-side path/logging the planner's own confirm does.
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [usedAckItems, setUsedAckItems] = useState(null);
    const [usedAckRetry, setUsedAckRetry] = useState(null);

    // Owner, 2026-09-01: batch confirm. Selection is keyed by planHash (the row identity the
    // board already uses); the confirms themselves are ONE strictly sequential server-side batch
    // through the same guarded endpoint as every per-row Confirm.
    const [batchSelection, setBatchSelection] = useState(() => new Map());
    const [batchRunning, setBatchRunning] = useState(false);
    const [batchOutcomes, setBatchOutcomes] = useState(null);
    const toggleBatchRow = useCallback((row) => {
        setBatchSelection(prev => {
            const next = new Map(prev);
            if (next.has(row.planHash)) next.delete(row.planHash); else next.set(row.planHash, row);
            return next;
        });
    }, []);

    const startBatchConfirm = useCallback(async () => {
        const picked = Array.from(batchSelection.values()).filter(batchEligible);
        if (picked.length === 0) return;
        if (!window.confirm(batchConfirmPrompt(picked.length))) return;
        setBatchRunning(true);
        setBatchOutcomes(null);
        try {
            // Poll until the sequential worker finishes -- each confirm is a real Amazon commit,
            // so a long batch legitimately takes minutes.
            await postAndPollBatch(
                picked.map(r => ({
                    runId: r.runId, planId: r.planId, placementOptionId: r.placementOptionId,
                })),
                setBatchOutcomes);
            setBatchSelection(new Map());
            await load();
            if (onRunsChanged) onRunsChanged();
        } catch (err) {
            console.error('Batch confirm failed to start:', err);
            toast.error('Batch confirm failed to start: ' + (err.response?.data?.error || err.message));
        }
        setBatchRunning(false);
    }, [batchSelection, load, onRunsChanged]);

    const openConfirm = useCallback((row) => setConfirmTarget(row), []);
    const closeConfirm = useCallback(() => setConfirmTarget(null), []);

    const doConfirm = useCallback(async (row, acknowledgeUsedCondition = false) => {
        if (!row || !boardConfirmable(row) || boardNeedsCarrierSelection(row)) return;
        // eslint-disable-next-line no-console
        console.log('[Recommended board] confirm requested', {
            runId: row.runId, planId: row.planId, placementOptionId: row.placementOptionId, acknowledgeUsedCondition,
        });
        setConfirming(true);
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm', {
                planId: row.planId,
                placementOptionId: row.placementOptionId,
                acknowledgeUsedCondition,
            }, { timeout: 120000 });
            setUsedAckItems(null);
            setUsedAckRetry(null);
            setConfirmTarget(null);
            const count = res.data?.shipmentCount;
            toast.success(`Confirmed — Run #${row.runId}${count != null ? ` · ${count} shipment(s)` : ''} sent to Amazon.`);
            await load();
            if (onRunsChanged) onRunsChanged();
        } catch (err) {
            // Task 80: same used-condition gate the planner's own confirm buttons hit -- show the
            // offending SKUs and require the explicit tick before retrying.
            const blocked = usedConditionBlockFrom(err);
            if (blocked) {
                setUsedAckItems(blocked);
                setUsedAckRetry(() => () => doConfirm(row, true));
                setConfirming(false);
                return;
            }
            console.error('Board confirm failed:', err);
            // Never a silent no-op -- surface the backend message verbatim.
            toast.error('Confirmation failed: ' + (err.response?.data?.error || err.message));
        }
        setConfirming(false);
    }, [load, onRunsChanged]);

    // Owner, 2026-09-01: the workspace's set-cover suggestion, applied as checkbox selection.
    // DEPTH_NOT_REACH guidance (the B07H4YSH3Y shape) surfaces as a message instead of a set --
    // suggesting rows there would imply reach is buyable when every draw is the same FC pair.
    const selectSuggested = useCallback(async (asin, group) => {
        try {
            const units = (group?.best?.totalUnits) || 0;
            const res = await ApiClient.get(
                `/api/shipment-planner/placement-suggestions/${encodeURIComponent(asin)}`
                + (units > 0 ? `?units=${units}` : ''), { timeout: 180000 });
            if (res.data?.guidance?.message) {
                toast(res.data.guidance.message, { icon: '📦', duration: 8000 });
                return;
            }
            const suggestion = (res.data?.suggestions || [])[0];
            const runIds = new Set((suggestion?.runIds || []).map(Number));
            if (runIds.size === 0) {
                toast('No suggested set — every open plan overlaps your current coverage.');
                return;
            }
            const eligible = (group?.rows || []).filter(r => runIds.has(Number(r.runId)) && batchEligible(r));
            if (eligible.length === 0) {
                toast('The suggested plans are not confirmable from the board — open them on the planner.');
                return;
            }
            setBatchSelection(prev => {
                const next = new Map(prev);
                eligible.forEach(r => next.set(r.planHash, r));
                return next;
            });
            toast.success(`Selected ${eligible.length} suggested plan${eligible.length !== 1 ? 's' : ''} — ${suggestion.label || suggestion.key}`);
        } catch (err) {
            console.error('Suggestion fetch failed:', err);
            toast.error('Could not fetch suggestions: ' + (err.response?.data?.error || err.message));
        }
    }, []);

    const actionable = rows.filter(r => r.confirmable).length;
    const batchCount = batchSelection.size;

    return (
        <div className="mb-6">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-md hover:bg-emerald-100"
            >
                <div className="flex items-center gap-2">
                    <StarIcon className="h-5 w-5 text-emerald-600" />
                    <span className="font-semibold text-gray-900">Recommended</span>
                    {!loading && actionable > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                            {actionable} confirmable
                        </span>
                    )}
                    <span className="text-xs text-gray-500">
                        best option per shipment, across every run of it in the last 3 days
                    </span>
                </div>
                <span className="text-gray-400 text-sm">{expanded ? '▲ collapse' : '▼ expand'}</span>
            </button>

            {expanded && (
                <div className="mt-3">
                    {/* Owner, 2026-09-01: the batch bar. Confirms run ONE at a time server-side
                        through the same guarded endpoint as every Confirm button -- see
                        ConfirmBatchService for why that is the whole design. */}
                    <BatchConfirmBar
                        count={batchCount}
                        running={batchRunning}
                        outcomes={batchOutcomes}
                        onStart={startBatchConfirm}
                        onClear={() => setBatchSelection(new Map())}
                        onDismiss={() => setBatchOutcomes(null)}
                    />
                    {loading && <div className="text-gray-500 text-sm px-1">Loading recommendations…</div>}
                    {error && <div className="text-red-600 text-sm px-1">Error: {error}</div>}
                    {!loading && !error && rows.length === 0 && (
                        <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-4 py-6 text-center">
                            No actionable options yet — analyze a shipment or let the probe draw.
                        </div>
                    )}
                    {!loading && !error && rows.length > 0 && (
                        <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Shipment</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Best option</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Destinations</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Freight + fee</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Mode</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Units / Boxes</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Drawn</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                        {/* Task 86: sticky to match the row cells below -- see the width-budget
                                            note above RecommendedSection for why this table can still need to
                                            scroll on a narrower screen than it was designed for. */}
                                        <th className="sticky right-0 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 border-l border-gray-200">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {groups.map(g => (
                                        g.optionsCount === 1 ? (
                                            <RecommendedRow
                                                key={g.rows[0].planHash}
                                                row={g.rows[0]}
                                                onOpen={openRun}
                                                onCancel={cancelPlan}
                                                onConfirm={openConfirm}
                                                cancelling={cancelling}
                                                expanded={expandedRows.has(g.rows[0].planHash)}
                                                onToggleExpand={() => toggleRowExpand(g.rows[0].planHash)}
                                                usedSkuAlerts={usedSkuAlerts.get(String(g.rows[0].runId))}
                                                batchSelected={batchSelection.has(g.rows[0].planHash)}
                                                onToggleBatch={toggleBatchRow}
                                            />
                                        ) : (
                                            <RecommendedGroupRow
                                                key={g.key}
                                                group={g}
                                                onOpen={openRun}
                                                onCancel={cancelPlan}
                                                onConfirm={openConfirm}
                                                cancelling={cancelling}
                                                expanded={expandedGroups.has(g.key)}
                                                onToggleExpand={() => toggleGroupExpand(g.key)}
                                                usedSkuAlerts={g.rows.flatMap(r => usedSkuAlerts.get(String(r.runId)) || [])}
                                                batchSelected={batchSelection.has(g.best.planHash)}
                                                onToggleBatch={toggleBatchRow}
                                                onSelectSuggested={selectSuggested}
                                            />
                                        )
                                    ))}
                                    {/* Task 82: multi-product compositions have no single SKU to group by --
                                        kept as their existing per-composition rows in their own section. */}
                                    {multi.length > 0 && (
                                        <>
                                            <tr>
                                                <td colSpan={9} className="px-3 py-1.5 bg-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                                    Multi-product shipments
                                                </td>
                                            </tr>
                                            {multi.map(row => (
                                                <RecommendedRow
                                                    key={row.planHash}
                                                    row={row}
                                                    onOpen={openRun}
                                                    onCancel={cancelPlan}
                                                    onConfirm={openConfirm}
                                                    cancelling={cancelling}
                                                    expanded={expandedRows.has(row.planHash)}
                                                    onToggleExpand={() => toggleRowExpand(row.planHash)}
                                                    batchSelected={batchSelection.has(row.planHash)}
                                                    onToggleBatch={toggleBatchRow}
                                                    usedSkuAlerts={usedSkuAlerts.get(String(row.runId))}
                                                />
                                            ))}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <RecommendedConfirmModal
                open={!!confirmTarget}
                row={confirmTarget}
                onCancel={closeConfirm}
                onConfirm={() => doConfirm(confirmTarget)}
                busy={confirming}
            />
            <UsedConditionAckModal
                open={!!usedAckItems}
                items={usedAckItems}
                onCancel={() => { setUsedAckItems(null); setUsedAckRetry(null); }}
                onAcknowledge={() => usedAckRetry && usedAckRetry()}
                busy={confirming}
            />
        </div>
    );
}

// ---------- Task 33 (background watch list) ----------
// Owner (2026-08-17): "implement it as background job now, it will time out with the browser.
// the browser should be able to kickoff the queries and the background job resumes the check
// every 6 hrs for unconfirmed shipments". Backed by GET /api/shipment-planner/watch.

function WatchStatusPill({ status }) {
    const s = String(status || '').toUpperCase();
    let cls = 'bg-gray-100 text-gray-600';
    if (s === 'ACTIVE') cls = 'bg-purple-100 text-purple-800';
    else if (s === 'CONFIRMED') cls = 'bg-green-100 text-green-800';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cls}`}>{s}</span>;
}

// Task 44 (openPlans UI): GET /api/shipment-planner/watch rows now carry `openPlans` --
// task-42's keep-BEST retention, up to MAX_OPEN_PLANS_PER_ASIN (3) still-confirmable plans per
// watched ASIN (typically the best-scoring valid plan + the latest draw). Each entry is
// {runId, planId, score, drawnAt, expiresInHours}.
function drawnAgoLabel(drawnAt) {
    if (!drawnAt) return null;
    const ms = Date.now() - new Date(drawnAt).getTime();
    if (isNaN(ms)) return null;
    const hours = ms / 3_600_000;
    if (hours < 1) return `${Math.max(0, Math.round(ms / 60000))}m ago`;
    return `${Math.round(hours)}h ago`;
}

function expiryLabel(expiresInHours) {
    if (expiresInHours === null || expiresInHours === undefined) return null;
    if (expiresInHours <= 0) return 'expiring now';
    return `expires in ${expiresInHours}h`;
}

function isExpiryAmber(expiresInHours) {
    return expiresInHours !== null && expiresInHours !== undefined && expiresInHours < 6;
}

function bestOpenPlanRunId(plans) {
    let best = null;
    for (const p of plans) {
        if (p.score === null || p.score === undefined) continue;
        if (!best || p.score > best.score) best = p;
    }
    return best ? best.runId : null;
}

function latestOpenPlanRunId(plans) {
    let latest = null;
    for (const p of plans) {
        if (!p.drawnAt) continue;
        const t = new Date(p.drawnAt).getTime();
        if (isNaN(t)) continue;
        if (!latest || t > latest.t) latest = { runId: p.runId, t };
    }
    return latest ? latest.runId : null;
}

function OpenPlanChip({ plan, isBest, isLatest, onOpen }) {
    const drawn = drawnAgoLabel(plan.drawnAt);
    const expiry = expiryLabel(plan.expiresInHours);
    const amber = isExpiryAmber(plan.expiresInHours);
    return (
        <button
            onClick={() => onOpen(plan.runId)}
            title={plan.planId || ''}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-200 text-xs whitespace-nowrap"
        >
            <span className="font-medium text-gray-700">
                {plan.score !== null && plan.score !== undefined ? plan.score.toFixed(1) : '—'}
            </span>
            {drawn && <span className="text-gray-400">{drawn}</span>}
            {expiry && <span className={amber ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{expiry}</span>}
            {isBest && <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold">best</span>}
            {isLatest && <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 text-[10px] font-semibold">latest</span>}
        </button>
    );
}

function OpenPlansCell({ openPlans, onOpen }) {
    if (!openPlans || openPlans.length === 0) return null;

    // Single open plan: keep it to one unobtrusive line, no best/latest tags (redundant with
    // only one candidate).
    if (openPlans.length === 1) {
        const plan = openPlans[0];
        const drawn = drawnAgoLabel(plan.drawnAt);
        const expiry = expiryLabel(plan.expiresInHours);
        const amber = isExpiryAmber(plan.expiresInHours);
        return (
            <button
                onClick={() => onOpen(plan.runId)}
                title={plan.planId || ''}
                className="flex items-center gap-2 text-xs text-gray-700 hover:text-indigo-700"
            >
                <span className="font-medium">
                    {plan.score !== null && plan.score !== undefined ? plan.score.toFixed(1) : '—'}
                </span>
                {drawn && <span className="text-gray-400">{drawn}</span>}
                {expiry && <span className={amber ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{expiry}</span>}
            </button>
        );
    }

    const bestId = bestOpenPlanRunId(openPlans);
    const latestId = latestOpenPlanRunId(openPlans);
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {openPlans.map(plan => (
                <OpenPlanChip
                    key={plan.runId}
                    plan={plan}
                    isBest={plan.runId === bestId}
                    isLatest={plan.runId === latestId}
                    onOpen={onOpen}
                />
            ))}
        </div>
    );
}

function WatchRow({ row, onStop, onOpenLatest, onOpenRun, stopping }) {
    const bd = row.bestDraw;
    const hasOpenPlans = row.openPlans && row.openPlans.length > 0;
    const confirmed = String(row.status).toUpperCase() === 'CONFIRMED';
    return (
        <tr className={`group ${confirmed ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
            <td className="px-3 py-2">
                <div className="font-mono text-xs text-gray-700">{row.asin}</div>
                {/* Task 43: product name under the ASIN (server-side batched title lookup) */}
                {row.name && (
                    <div className="text-xs text-gray-500 truncate max-w-[16rem]" title={row.name}>
                        {row.name}
                    </div>
                )}
            </td>
            <td className="px-3 py-2"><WatchStatusPill status={row.status} /></td>
            <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap"
                title={row.chunkSpec ? 'This row re-draws the whole broken-up set each cycle' : undefined}>
                {watchRowShapeLabel(row)}
            </td>
            <td className="px-3 py-2 text-right text-gray-700">{row.drawCount ?? 0}</td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-xs">{formatDateTime(row.lastDrawAt)}</td>
            <td className="px-3 py-2">
                {confirmed ? (
                    <span className="text-xs text-green-700 font-mono" title={row.confirmedPlanId || ''}>
                        plan {truncate(row.confirmedPlanId, 18) || '—'}{row.confirmedAt ? ` · ${formatDateTime(row.confirmedAt)}` : ''}
                    </span>
                ) : hasOpenPlans ? (
                    <OpenPlansCell openPlans={row.openPlans} onOpen={onOpenRun} />
                ) : bd ? (
                    <span className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-gray-700 font-medium">{bd.score !== null && bd.score !== undefined ? bd.score.toFixed(1) : '—'}</span>
                        <DecisionPill decision={bd.decision} />
                        <DestinationFcs destinationFcs={bd.destinationFcs} neededFcHits={[]} />
                        <DrawPercentileChip percentile={bd.drawPercentile} sampleSize={bd.drawSampleSize} />
                    </span>
                ) : (
                    <span className="text-xs text-gray-400">no draw yet</span>
                )}
                {row.note && <span className="ml-2 text-xs text-amber-600">{row.note}</span>}
            </td>
            <td className={`sticky right-0 px-3 py-2 border-l border-gray-100 ${confirmed ? 'bg-green-50 group-hover:bg-green-50' : 'bg-white group-hover:bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                    {row.lastRunId && (
                        <button
                            onClick={() => onOpenLatest(row)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                            Open latest
                        </button>
                    )}
                    {!confirmed && (
                        <button
                            onClick={() => onStop(row)}
                            disabled={stopping === row.id}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                        >
                            {stopping === row.id ? 'Stopping…' : 'Stop'}
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function WatchListSection({ navigate, pollMs, onCountChange }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [showConfirmed, setShowConfirmed] = useState(false);
    const [stopping, setStopping] = useState(null);
    // 2026-09-03 collapse work: report the ACTIVE count for the wrapper's chip.
    useEffect(() => {
        if (onCountChange) onCountChange(rows.filter(r => String(r.status).toUpperCase() === 'ACTIVE').length);
    }, [rows, onCountChange]);
    // Task 43: auto-expand only on the FIRST load -- background polls must not fight the user's
    // collapse/expand choice.
    const initializedRef = useRef(false);

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await ApiClient.get('/api/shipment-planner/watch');
            const data = res.data || [];
            setRows(data);
            setError(null);
            if (!initializedRef.current) {
                initializedRef.current = true;
                setExpanded(data.length > 0);
            }
        } catch (err) {
            console.error('Failed to load watch list:', err);
            if (!background) {
                setError(err.response?.data?.error || err.message || 'Failed to load watch list');
            }
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const backgroundLoad = useCallback(() => load({ background: true }), [load]);
    useVisiblePolling(backgroundLoad, pollMs);

    const stopWatch = async (row) => {
        if (!window.confirm(`Stop watching ${row.asin}? The 6-hourly draws for it will end.`)) return;
        setStopping(row.id);
        try {
            await ApiClient.post(`/api/shipment-planner/watch/${row.id}/stop`);
            await load();
        } catch (err) {
            console.error('Stop watch failed:', err);
            alert('Stop failed: ' + (err.response?.data?.error || err.message));
        }
        setStopping(null);
    };

    const openLatest = (row) => navigate(`/shipment-planner?run=${row.lastRunId}`);
    const openRun = (runId) => navigate(`/shipment-planner?run=${runId}`);

    const active = rows.filter(r => String(r.status).toUpperCase() === 'ACTIVE');
    const confirmedRows = rows.filter(r => String(r.status).toUpperCase() === 'CONFIRMED');

    if (!loading && !error && rows.length === 0) return null;

    return (
        <div className="mb-6">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 border border-purple-100 rounded-md hover:bg-purple-100"
            >
                <div className="flex items-center gap-2">
                    <EyeIcon className="h-5 w-5 text-purple-600" />
                    <span className="font-semibold text-gray-900">Watch list</span>
                    {!loading && active.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-purple-600 text-white text-xs font-semibold">
                            {active.length} watching
                        </span>
                    )}
                    <span className="text-xs text-gray-500">drawn every 6h until confirmed</span>
                </div>
                <span className="text-gray-400 text-sm">{expanded ? '▲ collapse' : '▼ expand'}</span>
            </button>

            {expanded && (
                <div className="mt-3">
                    {loading && <div className="text-gray-500 text-sm px-1">Loading watch list…</div>}
                    {error && <div className="text-red-600 text-sm px-1">Error: {error}</div>}
                    {!loading && !error && rows.length > 0 && (
                        <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">ASIN / Product</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Qty</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Draws</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Last draw</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Best draw</th>
                                        <th className="sticky right-0 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 border-l border-gray-200">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {active.map(row => (
                                        <WatchRow key={row.id} row={row} onStop={stopWatch}
                                                  onOpenLatest={openLatest} onOpenRun={openRun} stopping={stopping} />
                                    ))}
                                    {showConfirmed && confirmedRows.map(row => (
                                        <WatchRow key={row.id} row={row} onStop={stopWatch}
                                                  onOpenLatest={openLatest} onOpenRun={openRun} stopping={stopping} />
                                    ))}
                                </tbody>
                            </table>
                            {confirmedRows.length > 0 && (
                                <button
                                    onClick={() => setShowConfirmed(prev => !prev)}
                                    className="w-full px-3 py-2 text-xs text-green-700 bg-green-50 hover:bg-green-100 border-t border-gray-200 text-left"
                                >
                                    {showConfirmed ? '▲ hide' : '▼ show'} {confirmedRows.length} confirmed
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- formatting helpers ----------

// ---- Task 53: partition sweeps (owner: "we need the ability to look at shipments broken apart
// and together to examine the best placement ... same 24 units just broken apart") ----

function sweepMoney(v) {
    return (v === null || v === undefined) ? '—' : `$${Number(v).toFixed(2)}`;
}

function SweepStatusPill({ status }) {
    const s = String(status || '').toUpperCase();
    let cls = 'bg-gray-100 text-gray-600';
    if (s === 'RUNNING') cls = 'bg-amber-100 text-amber-800';
    else if (s === 'DONE') cls = 'bg-green-100 text-green-800';
    else if (s === 'FAILED') cls = 'bg-red-100 text-red-800';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{s || '?'}</span>;
}

/** One expanded sweep's variant comparison table -- variant, chunks (qty → FCs, per-chunk Open
 * links), distinct FCs, placement fees (the 5-box fee-break column), freight, combined $/mo,
 * combined score; winner highlighted. */
function SweepVariantTable({ results, onOpenRun }) {
    const variants = results?.variants || [];
    if (variants.length === 0) return <div className="text-sm text-gray-500 px-2 py-1">No variant results.</div>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Variant</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Chunks (qty → FCs)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">FCs</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Placement fees</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Freight</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">$/mo</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Score</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Notes</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {variants.map(v => {
                        const t = v.variantTotals || {};
                        return (
                            <tr key={v.name} className={v.winner ? 'bg-emerald-50 font-semibold' : ''}>
                                <td className="px-3 py-2 whitespace-nowrap">{v.winner ? '★ ' : ''}{v.name}</td>
                                <td className="px-3 py-2">
                                    {(v.chunks || []).map((c, i) => (
                                        <div key={i} className="whitespace-nowrap">
                                            {c.qty} →{' '}
                                            {c.error
                                                ? <span className="text-red-600 font-normal">failed: {c.error}</span>
                                                : <>
                                                    {(c.destinationFcs || []).join(', ') || '—'}
                                                    {c.runId && (
                                                        <button
                                                            onClick={() => onOpenRun(c.runId)}
                                                            className="ml-2 text-indigo-600 hover:underline text-xs font-normal"
                                                        >
                                                            Open
                                                        </button>
                                                    )}
                                                </>}
                                        </div>
                                    ))}
                                </td>
                                <td className="px-3 py-2 text-right">{t.distinctFcs ?? '—'}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">{sweepMoney(t.totalPlacementFees)}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">{sweepMoney(t.totalFreight)}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">{sweepMoney(t.combinedValuePerMonth)}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    {t.combinedScore !== null && t.combinedScore !== undefined ? Number(t.combinedScore).toFixed(1) : '—'}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-600 font-normal max-w-xs">
                                    {[v.feeBreakNote, v.generatorNote].filter(Boolean).join('; ') || '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function SweepRow({ sweep, onOpenRun }) {
    const [open, setOpen] = useState(false);
    const results = sweep.results;
    return (
        <>
            <tr className="hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">#{sweep.id}</td>
                <td className="px-3 py-2 font-mono text-xs">{sweep.asin}</td>
                <td className="px-3 py-2 text-right">{sweep.qty}</td>
                <td className="px-3 py-2"><SweepStatusPill status={sweep.status} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-500">{formatDateTime(sweep.createdAt)}</td>
                <td className="px-3 py-2">
                    {String(sweep.status).toUpperCase() === 'RUNNING'
                        ? <span className="text-amber-600 text-xs">analyzing chunks… (email on completion)</span>
                        : results?.recommended
                            ? <span className="text-emerald-700 font-medium">★ {results.recommended}</span>
                            : results?.error
                                ? <span className="text-red-600 text-xs">{truncate(String(results.error), 60)}</span>
                                : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                    {results && (
                        <button onClick={() => setOpen(prev => !prev)} className="text-indigo-600 hover:underline text-xs">
                            {open ? 'hide' : 'details'}
                        </button>
                    )}
                </td>
            </tr>
            {open && results && (
                <tr>
                    <td colSpan={7} className="px-3 py-3 bg-gray-50">
                        {results.recommendation && (
                            <p className="text-sm text-gray-800 mb-1">{results.recommendation}</p>
                        )}
                        {results.togetherNote && (
                            <p className="text-xs text-gray-600 italic mb-1">{results.togetherNote}</p>
                        )}
                        {results.remainderNote && (
                            <p className="text-xs text-amber-700 mb-1">{results.remainderNote}</p>
                        )}
                        <SweepVariantTable results={results} onOpenRun={onOpenRun} />
                        <p className="text-xs text-gray-500 mt-2">
                            All chunk plans are UNCONFIRMED — open the winning chunks to confirm them, and cancel the rest.
                        </p>
                    </td>
                </tr>
            )}
        </>
    );
}


// ---------------------------------------------------------------------------
// Task 103 — the planner job queue. Owner, verbatim: "can you show the queued jobs in the shipment
// runs so that there is visibility to them?"
//
// Analyzes run on the server now, so the browser is no longer where the work lives — which is the
// whole point, but it also means an analyze can succeed, fail, or be killed by a deploy with
// nothing on screen anywhere. On 2026-08-21 an operator's three-way split was interrupted by a
// service restart and he had no idea until someone queried the table by hand. This section is the
// answer: what is running, what just landed, and every failure that has not been dealt with.
// ---------------------------------------------------------------------------

/** Elapsed time for a job, live while it runs and frozen once it ends. */
export function jobElapsedLabel(job, now = Date.now()) {
    const start = job?.startedAt || job?.createdAt;
    if (!start) return null;
    const end = job?.finishedAt ? new Date(job.finishedAt).getTime() : now;
    const ms = end - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 60000) return `${Math.max(1, Math.round(ms / 1000))}s`;
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins}m ${Math.round((ms % 60000) / 1000)}s`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** Human name for a job type. Falls back to the raw token rather than inventing a label. */
export function jobTypeLabel(type) {
    return {
        SPLIT_SET_ANALYZE: 'Split set analyze',
        ANALYZE_ASIN_LIST: 'ASIN list analyze',
        ANALYZE_PO: 'PO analyze',
    }[type] || type || 'analyze';
}

/** What a job belongs to, in the operator's words, plus a link when there is somewhere to go. */
export function jobScopeLabel(job) {
    if (job?.scopeType === 'SPLIT_GROUP') return { text: `Split set ${job.scopeKey}`, href: `/split-sets/${encodeURIComponent(job.scopeKey)}` };
    if (job?.scopeType === 'DRAFT') return { text: `Shipment draft ${job.scopeKey}`, href: null };
    if (job?.scopeType === 'PLANNER') return { text: 'PO-mode planner', href: null };
    return { text: job?.scopeKey || '—', href: null };
}

export function jobPhasePill(phase) {
    return {
        QUEUED: 'bg-gray-200 text-gray-700',
        RUNNING: 'bg-amber-500 text-white',
        DONE: 'bg-green-100 text-green-800',
        FAILED: 'bg-red-100 text-red-800',
        INTERRUPTED: 'bg-orange-100 text-orange-900',
    }[phase] || 'bg-gray-100 text-gray-600';
}

/**
 * The one-line truth about a job. INTERRUPTED deliberately reads "N never ran" rather than
 * borrowing the failure wording — "Amazon refused it" and "we never asked" are different problems
 * and only the second is fixed by pressing retry.
 */
export function jobSummaryLine(job) {
    if (!job) return '';
    if (job.phase === 'QUEUED') {
        const ahead = job.queuePosition;
        return typeof ahead === 'number' && ahead > 0
            ? `Waiting — ${ahead} analyze${ahead === 1 ? '' : 's'} ahead of it on the worker.`
            : 'Waiting — next to start.';
    }
    if (job.phase === 'RUNNING') {
        return job.total > 1
            ? `Analyzing shipment ${Math.min((job.done || 0) + 1, job.total)} of ${job.total}.`
            : 'Analyzing.';
    }
    if (job.phase === 'INTERRUPTED') {
        const missed = job.interruptedCount || 0;
        return `Stopped by a server restart — ${job.done || 0} of ${job.total} analyzed`
            + (missed > 0 ? `, ${missed} never ran` : '')
            + '. Nothing was confirmed.';
    }
    if (job.phase === 'FAILED') return job.error || 'The analyze failed.';
    const failed = job.failedCount || 0;
    return failed > 0
        ? (job.result?.summary || `${job.done || 0} of ${job.total} analyzed, ${failed} failed.`)
        : `${job.done || 0} of ${job.total} analyzed. Nothing was confirmed.`;
}

/** True for a job the operator still has to do something about. */
export function jobNeedsAttention(job) {
    return job?.phase === 'FAILED' || job?.phase === 'INTERRUPTED'
        || (job?.phase === 'DONE' && (job.failedCount || 0) > 0);
}

function PlannerJobRow({ job, onOpenRun, onRetry, onDismiss, busyId }) {
    const scope = jobScopeLabel(job);
    const elapsed = jobElapsedLabel(job);
    const attention = jobNeedsAttention(job);
    const retryable = job.phase === 'FAILED' || job.phase === 'INTERRUPTED';
    const runSteps = (job.steps || []).filter(st => st.runId != null);
    return (
        <tr className={attention ? 'bg-red-50/40' : 'hover:bg-gray-50'} data-testid={`planner-job-${job.jobId}`}>
            <td className="px-3 py-2">
                <div className="text-gray-900">{job.label || jobTypeLabel(job.type)}</div>
                <div className="text-[11px] text-gray-400">{jobTypeLabel(job.type)} · job #{job.jobId}</div>
            </td>
            <td className="px-3 py-2 text-xs">
                {scope.href ? (
                    <a href={scope.href} className="text-indigo-600 hover:text-indigo-800 hover:underline">{scope.text}</a>
                ) : <span className="text-gray-600">{scope.text}</span>}
            </td>
            <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${jobPhasePill(job.phase)}`}>
                    {String(job.phase || '').toLowerCase()}
                </span>
            </td>
            <td className="px-3 py-2 text-sm whitespace-nowrap">
                {job.phase === 'QUEUED' ? <span className="text-gray-400">not started</span>
                    : <>{job.done || 0}/{job.total || 0}</>}
                {(job.failedCount || 0) > 0 && (
                    <span className="ml-1 text-[11px] text-red-700">{job.failedCount} failed</span>
                )}
            </td>
            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{elapsed || '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-700 max-w-md">
                {jobSummaryLine(job)}
                {runSteps.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                        {runSteps.map(st => (
                            <button
                                key={st.index}
                                onClick={() => onOpenRun(st.runId)}
                                title={st.analysisError || st.label || `run ${st.runId}`}
                                className={`px-1.5 py-0.5 rounded border text-[10px] ${st.state === 'FAILED'
                                    ? 'border-red-300 text-red-700 hover:bg-red-100'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                            >
                                run {st.runId}{st.state === 'FAILED' ? ' — failed' : ''}
                            </button>
                        ))}
                    </div>
                )}
            </td>
            <td className="px-3 py-2 whitespace-nowrap text-right">
                {retryable && (
                    <button
                        onClick={() => onRetry(job)}
                        disabled={busyId === job.jobId}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline disabled:opacity-50"
                    >
                        {busyId === job.jobId ? 'Retrying…' : 'Retry'}
                    </button>
                )}
                {job.phase !== 'QUEUED' && job.phase !== 'RUNNING' && (
                    <button
                        onClick={() => onDismiss(job)}
                        disabled={busyId === job.jobId}
                        className="ml-2 text-xs text-gray-400 hover:text-gray-600 hover:underline disabled:opacity-50"
                        title="Hide this from the queue. Nothing else changes."
                    >
                        Dismiss
                    </button>
                )}
            </td>
        </tr>
    );
}

// Compact duration for the History view: 150 -> "2m 30s", 45 -> "45s", 3700 -> "1h 1m".
export function historyDurationLabel(seconds) {
    if (seconds == null || Number.isNaN(Number(seconds))) return '—';
    const s = Math.max(0, Math.floor(Number(seconds)));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

export function PlannerJobsSection({ navigate, pollMs, onCountChange }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [busyId, setBusyId] = useState(null);
    // Owner, 2026-09-05: "we need a a way to analyse the existing data" -- History mode renders
    // every job ever run, from the same tables, without touching the Live queue's behavior.
    const [view, setView] = useState('live');
    const [history, setHistory] = useState(null);
    const [historyError, setHistoryError] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [histStatus, setHistStatus] = useState('all');
    const [histQ, setHistQ] = useState('');
    const [histPage, setHistPage] = useState(1);

    const loadHistory = useCallback(async ({ status = 'all', q = '', page = 1 } = {}) => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const params = new URLSearchParams({ page: String(page) });
            if (status && status !== 'all') params.set('status', status);
            if (q) params.set('q', q);
            const res = await ApiClient.get(`/api/shipment-planner/jobs/history?${params.toString()}`);
            setHistory(res.data || null);
        } catch (err) {
            console.error('Failed to load job history:', err);
            setHistoryError(err.response?.data?.error || err.message || 'Failed to load job history');
        }
        setHistoryLoading(false);
    }, []);
    // 2026-09-03 collapse work: report the job count for the wrapper's chip. Owner, 2026-09-06
    // ("it is still showing zero jobs"): a bare 0 while 113 jobs sit in History reads as broken —
    // when nothing is live, the chip reports the history total instead.
    useEffect(() => {
        if (!onCountChange) return;
        if (jobs.length > 0) onCountChange(jobs.length);
        else if (history?.stats?.total != null) onCountChange(`${history.stats.total} total`);
        else onCountChange(jobs.length);
    }, [jobs, history, onCountChange]);

    // Owner, 2026-09-06: an empty first Live load auto-opens History — opening the section must
    // always show the record, not a quiet zero. Once only; after that the operator owns the tabs.
    const autoHistoryRef = useRef(false);

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) { setLoading(true); setError(null); }
        try {
            const res = await ApiClient.get('/api/shipment-planner/jobs/queue?limit=25');
            const list = Array.isArray(res.data) ? res.data : [];
            setJobs(list);
            setError(null);
            if (list.length === 0 && !autoHistoryRef.current) {
                autoHistoryRef.current = true;
                setView('history');
                loadHistory({});
            }
        } catch (err) {
            console.error('Failed to load planner jobs:', err);
            if (!background) setError(err.response?.data?.error || err.message || 'Failed to load analysis jobs');
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);
    const backgroundLoad = useCallback(() => load({ background: true }), [load]);
    useVisiblePolling(backgroundLoad, pollMs);

    const active = jobs.filter(j => j.phase === 'QUEUED' || j.phase === 'RUNNING');
    const attention = jobs.filter(jobNeedsAttention);

    const handleRetry = async (job) => {
        setBusyId(job.jobId);
        try {
            const res = await ApiClient.post(`/api/shipment-planner/jobs/${job.jobId}/retry`);
            toast.success(`Re-running as job #${res.data?.jobId}. Nothing is confirmed automatically.`);
            await load({ background: true });
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Retry failed');
        } finally {
            setBusyId(null);
        }
    };

    const handleDismiss = async (job) => {
        setBusyId(job.jobId);
        try {
            await ApiClient.post(`/api/shipment-planner/jobs/${job.jobId}/acknowledge`);
            await load({ background: true });
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Could not dismiss');
        } finally {
            setBusyId(null);
        }
    };

    // 2026-09-05: the section no longer vanishes when Live is empty -- History must stay
    // reachable. An empty Live view renders quiet (one line, no table) instead of disappearing.
    const switchView = (next) => {
        setView(next);
        if (next === 'history') loadHistory({ status: histStatus, q: histQ.trim(), page: 1 });
    };
    const applyHistoryFilter = (status) => {
        setHistStatus(status);
        setHistPage(1);
        loadHistory({ status, q: histQ.trim(), page: 1 });
    };
    const applyHistorySearch = () => {
        setHistPage(1);
        loadHistory({ status: histStatus, q: histQ.trim(), page: 1 });
    };
    const goHistoryPage = (page) => {
        setHistPage(page);
        loadHistory({ status: histStatus, q: histQ.trim(), page });
    };
    const stats = history?.stats;
    const historyJobs = history?.jobs || [];
    const lastPage = history ? Math.max(1, Math.ceil((history.total || 0) / (history.pageSize || 25))) : 1;

    return (
        <div className="mb-6" data-testid="planner-jobs-section">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100"
            >
                <div className="flex items-center gap-2">
                    {/* Named "Analysis jobs", NOT a queue: the bottom table is the owner's
                        "Shipment Queue", and two things called a queue on one page is a reading
                        hazard. This section is analysis WORK; that one is the shipments. */}
                    <span className="font-semibold text-gray-900">Analysis jobs</span>
                    {active.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold">
                            {active.length} running
                        </span>
                    )}
                    {attention.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-semibold"
                            data-testid="planner-jobs-attention">
                            {attention.length} needs attention
                        </span>
                    )}
                    <span className="text-xs text-gray-500">analyses running on the server — nothing here confirms</span>
                </div>
                <span className="text-gray-400 text-sm">{expanded ? '▲ collapse' : '▼ expand'}</span>
            </button>

            {expanded && (
                <div className="mt-3">
                    {/* Live = the queue as before; History = every job ever run (owner 2026-09-05:
                        "back fill it with the existing data"). */}
                    <div className="mb-2 inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-medium">
                        {[['live', 'Live'], ['history', 'History']].map(([value, label], idx) => (
                            <button key={value}
                                    onClick={() => switchView(value)}
                                    className={`px-3 py-1.5 ${idx > 0 ? 'border-l border-gray-300' : ''} ${
                                        view === value ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {view === 'history' && (
                        <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <select
                                    aria-label="History status filter"
                                    value={histStatus}
                                    onChange={e => applyHistoryFilter(e.target.value)}
                                    className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                                >
                                    <option value="all">All statuses</option>
                                    <option value="done">Done</option>
                                    <option value="failed">Failed</option>
                                    <option value="interrupted">Interrupted</option>
                                </select>
                                <input
                                    value={histQ}
                                    onChange={e => setHistQ(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && applyHistorySearch()}
                                    placeholder="Search label, scope, or error…"
                                    className="border border-gray-300 rounded-md px-2 py-1 text-xs w-64"
                                />
                                <button onClick={applyHistorySearch}
                                        className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                    Search
                                </button>
                            </div>
                            {historyLoading && <div className="text-gray-500 text-sm px-1">Loading history…</div>}
                            {historyError && <div className="text-red-600 text-sm px-1">Error: {historyError}</div>}
                            {!historyLoading && !historyError && stats && (
                                <div className="text-xs text-gray-600 mb-2 px-1" data-testid="history-stats">
                                    {stats.total} jobs · {stats.done} done · {stats.failed} failed · {stats.interrupted} interrupted
                                    {stats.medianDurationSeconds != null && (
                                        <> · median {historyDurationLabel(stats.medianDurationSeconds)}</>
                                    )}
                                </div>
                            )}
                            {!historyLoading && !historyError && historyJobs.length > 0 && (
                                <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Job</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Product / scope</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Outcome</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Duration</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Run</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {historyJobs.map(j => (
                                                <tr key={j.jobId} data-testid={`history-job-${j.jobId}`}>
                                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                                                        #{j.jobId}
                                                        <div className="text-[10px] text-gray-400">
                                                            {(j.createdAt || '').replace('T', ' ').replace('Z', '')}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="text-gray-900">{j.label || j.scopeKey}</div>
                                                        {(j.itemCode || j.productName) && (
                                                            <div className="text-[11px] text-gray-500 truncate max-w-[220px]"
                                                                 title={`${j.itemCode || ''} ${j.productName || ''}`.trim()}>
                                                                {j.itemCode}{j.itemCode && j.productName ? ' — ' : ''}{j.productName}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                            j.phase === 'DONE' ? 'bg-green-100 text-green-800'
                                                            : j.phase === 'FAILED' ? 'bg-red-100 text-red-800'
                                                            : j.phase === 'INTERRUPTED' ? 'bg-amber-100 text-amber-800'
                                                            : 'bg-gray-100 text-gray-600'}`}>
                                                            {j.phase}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 max-w-[320px]">
                                                        {j.error
                                                            ? <span className="text-red-700 text-xs block truncate" title={j.error}>{j.error}</span>
                                                            : <span className="text-gray-600 text-xs">
                                                                {j.done}/{j.total} analyzed{j.result?.decision ? ` · ${j.result.decision}` : ''}
                                                              </span>}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                                        {historyDurationLabel(j.durationSeconds)}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        {j.runId ? (
                                                            <button onClick={() => navigate(`/shipment-planner?run=${j.runId}`)}
                                                                    className="text-indigo-600 hover:text-indigo-800 text-xs underline">
                                                                run {j.runId}
                                                            </button>
                                                        ) : <span className="text-gray-300 text-xs">—</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {!historyLoading && !historyError && history && lastPage > 1 && (
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                                    <button disabled={histPage <= 1} onClick={() => goHistoryPage(histPage - 1)}
                                            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">← Newer</button>
                                    <span>page {histPage} of {lastPage}</span>
                                    <button disabled={histPage >= lastPage} onClick={() => goHistoryPage(histPage + 1)}
                                            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">Older →</button>
                                </div>
                            )}
                        </div>
                    )}

                    {view === 'live' && loading && <div className="text-gray-500 text-sm px-1">Loading analysis jobs…</div>}
                    {view === 'live' && error && <div className="text-red-600 text-sm px-1">Error: {error}</div>}
                    {view === 'live' && !loading && !error && jobs.length === 0 && (
                        <div className="text-gray-500 text-sm px-1">
                            No analyses running or awaiting attention — History has every past job.
                        </div>
                    )}
                    {view === 'live' && !loading && !error && jobs.length > 0 && (
                        <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Job</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Belongs to</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Progress</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Elapsed</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">What happened</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {jobs.map(j => (
                                        <PlannerJobRow
                                            key={j.jobId}
                                            job={j}
                                            onOpenRun={(runId) => navigate(`/shipment-planner?run=${runId}`)}
                                            onRetry={handleRetry}
                                            onDismiss={handleDismiss}
                                            busyId={busyId}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function PartitionSweepsSection({ navigate, pollMs, onCountChange }) {
    const [sweeps, setSweeps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const initializedRef = useRef(false);
    // 2026-09-03 collapse work: report the sweep count for the wrapper's chip.
    useEffect(() => { if (onCountChange) onCountChange(sweeps.length); }, [sweeps, onCountChange]);

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await ApiClient.get('/api/shipment-planner/partition-sweeps?limit=20');
            const data = res.data || [];
            setSweeps(data);
            setError(null);
            if (!initializedRef.current) {
                initializedRef.current = true;
                // Auto-expand only while a sweep is actively running -- finished sweeps stay
                // one click away rather than pushing the runs table down.
                setExpanded(data.some(s => String(s.status).toUpperCase() === 'RUNNING'));
            }
        } catch (err) {
            console.error('Failed to load partition sweeps:', err);
            if (!background) {
                setError(err.response?.data?.error || err.message || 'Failed to load partition sweeps');
            }
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);
    const backgroundLoad = useCallback(() => load({ background: true }), [load]);
    useVisiblePolling(backgroundLoad, pollMs);

    const openRun = (runId) => navigate(`/shipment-planner?run=${runId}`);
    const running = sweeps.filter(s => String(s.status).toUpperCase() === 'RUNNING');

    if (!loading && !error && sweeps.length === 0) return null;

    return (
        <div className="mb-6">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-cyan-50 border border-cyan-100 rounded-md hover:bg-cyan-100"
            >
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">Partition sweeps</span>
                    {!loading && running.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold">
                            {running.length} running
                        </span>
                    )}
                    <span className="text-xs text-gray-500">one ASIN broken into box chunks vs together</span>
                </div>
                <span className="text-gray-400 text-sm">{expanded ? '▲ collapse' : '▼ expand'}</span>
            </button>

            {expanded && (
                <div className="mt-3">
                    {loading && <div className="text-gray-500 text-sm px-1">Loading partition sweeps…</div>}
                    {error && <div className="text-red-600 text-sm px-1">Error: {error}</div>}
                    {!loading && !error && sweeps.length > 0 && (
                        <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Sweep</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">ASIN</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Qty</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Started</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Best variant</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {sweeps.map(s => <SweepRow key={s.id} sweep={s} onOpenRun={openRun} />)}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function truncate(s, n = 14) {
    if (!s) return '';
    return s.length > n ? s.slice(0, n) + '…' : s;
}

function ModeBadge({ mode }) {
    if (mode === 'PO') {
        return <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium">PO</span>;
    }
    if (mode === 'ASIN') {
        return <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">ASIN</span>;
    }
    if (mode === 'PROBE') {
        return <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-xs font-medium" title="6-hourly placement probe — analyzed then always voided, never confirmed">PROBE</span>;
    }
    if (mode === 'WATCH') {
        return <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-medium" title="Background watch draw — a real candidate plan, left unconfirmed for the operator (superseded watch plans are auto-cancelled)">WATCH</span>;
    }
    if (mode === 'SWEEP') {
        return <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 text-xs font-medium" title="Partition sweep chunk — one ASIN's qty broken into box-multiple chunks analyzed as separate plans, all left unconfirmed. See the Partition sweeps section.">SWEEP</span>;
    }
    return <span className="text-gray-400 text-xs">—</span>;
}

function StatusPill({ status }) {
    const s = String(status || '').toUpperCase();
    let cls = 'bg-gray-100 text-gray-600';
    if (s === 'ACTIVE') cls = 'bg-green-100 text-green-800';
    else if (s === 'CONFIRMED') cls = 'bg-blue-100 text-blue-800';
    else if (s === 'CANCELLED' || s === 'SUPERSEDED') cls = 'bg-gray-200 text-gray-600';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{s || 'UNKNOWN'}</span>;
}

// Milestone C task C4: the run row's planner_phase, beside the status pill -- so an operator
// scanning the runs table can find LTL runs still waiting on the warehouse (measured pallets,
// not yet re-quoted) without opening each one. PRICED (a requote persisted real freight) and null
// (parcel, and every pre-Milestone-C run) render nothing -- only AWAITING_PALLETS is a call to
// action here.
export function PhaseChip({ phase }) {
    if (phase !== 'AWAITING_PALLETS') return null;
    return (
        <span data-testid="phase-chip-awaiting"
            className="ml-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium whitespace-nowrap"
            title="This LTL run's freight has not been quoted yet -- pallets still need to be measured and re-quoted.">
            Awaiting pallets
        </span>
    );
}

const SORT_FIELDS = {
    date: (r) => new Date(r.created_at || 0).getTime(),
    status: (r) => String(r.status || ''),
    mode: (r) => String(r.source_mode || ''),
};

/**
 * Task 103 (owner: "add the FCs in another column on the shipment runs page") — where a run is
 * actually going, without opening it.
 *
 * Two rules the cell exists to keep:
 *  - The FCs are those of the placement the run STANDS BEHIND (confirmed, else recommended, else
 *    Amazon's first) — resolved server-side in DBService.choosePlacement. Showing the destinations
 *    of an option nobody chose would be a confident wrong answer about where inventory is going.
 *  - A run whose analysis FAILED has no destinations, and that is NOT the same as a run that went
 *    nowhere. It renders as a failure, never as an empty cell: on 2026-08-21 a dead three-way
 *    split read as "0 FCs reached, every option SKIP" and cost an hour.
 *
 * Row height stays stable: at most FC_CELL_VISIBLE codes, the rest behind a "+N" with the full
 * list on hover.
 */
export const FC_CELL_VISIBLE = 3;
/** FC codes shown inside one option chip before it collapses to "+N". */
export const FC_PER_OPTION_VISIBLE = 2;
/** Option chips shown inline before the rest collapse to "+N more". */
export const FC_OPTIONS_VISIBLE = 3;

/**
 * How one placement option should be marked. Owner 2026-08-21: "highlight the recommended one",
 * plus distinct marks for the confirmed option and the operator's own last selection.
 *
 * Three signals on one row is a real design problem, so the rules are deliberate:
 *
 *  - A CONFIRMED run is settled. Its confirmed option is the only marked one and everything else
 *    goes plain — nobody needs our recommendation highlighted on a shipment that already shipped.
 *  - Otherwise the recommendation is an OUTLINE (our opinion, lightly held) and the operator's own
 *    selection is a FILL (their decision, stated more firmly).
 *  - An EXPLICIT pick always renders as the fill — even when it is the recommended option.
 *    (Owner bug 2026-09-01: "when selecing the outlined recommend FC, nothing changes" — the
 *    original 2026-08-21 rule kept the outline when they agreed with us, which was fine for a
 *    display-only chip but reads as a dead click now that chips are pick buttons. Still one
 *    mark, never two: the agreement is noted in the label, not a second chip colour.)
 *
 * Outline / fill / solid-green are tellable apart at a glance without a legend, and at most two
 * chips in a row ever carry colour.
 */
export function placementMark(option, { anyConfirmed }) {
    if (!option) return null;
    if (option.confirmed) return { kind: 'confirmed', glyph: '✔', label: 'Confirmed — this is what Amazon is shipping' };
    if (anyConfirmed) return null;              // settled state dominates
    if (option.selected) {
        return { kind: 'selected', glyph: '✓', label: option.recommended
            ? 'You selected this — it is also the recommendation'
            : 'You selected this instead of the recommendation' };
    }
    if (option.recommended) return { kind: 'recommended', glyph: '★', label: 'Recommended' };
    return null;
}

const MARK_CLASS = {
    confirmed: 'bg-emerald-600 text-white border-emerald-600',
    selected: 'bg-indigo-600 text-white border-indigo-600',
    recommended: 'bg-white text-indigo-700 border-indigo-400 ring-1 ring-indigo-300',
};

/** "HEA2, BNA6 +2" — the compact body of one option chip. */
export function fcChipText(fcs) {
    const list = fcs || [];
    if (list.length === 0) return '—';
    const shown = list.slice(0, FC_PER_OPTION_VISIBLE);
    const overflow = list.length - shown.length;
    return shown.join(', ') + (overflow > 0 ? ` +${overflow}` : '');
}

/** "· $88.20 · TAKE" — the cost/verdict suffix for an option's hover title, only what is known. */
function optionTitleSuffix(option) {
    const bits = [];
    if (typeof option.totalCost === 'number') bits.push(`$${option.totalCost.toFixed(2)}`);
    if (option.decision) bits.push(option.decision);
    return bits.length ? ` · ${bits.join(' · ')}` : '';
}

function PlacementChip({ option, mark, index, locations, onPick }) {
    const failed = !!option.transportError;
    const cls = failed
        ? 'bg-white text-red-700 border-red-300 line-through decoration-red-400'
        : (mark ? MARK_CLASS[mark.kind] : 'bg-gray-50 text-gray-600 border-gray-200');
    const fcs = option.fcs || [];
    const shown = fcs.slice(0, FC_PER_OPTION_VISIBLE);
    const overflow = fcs.length - shown.length;
    // Chip-level title = which option this is. Each FC CODE carries its own city/state, so hovering
    // a specific code answers "where is that", which is what was actually asked for.
    const chipTitle = failed
        ? `Option ${index + 1}: not quoted — ${option.transportError}`
        : `Option ${index + 1}${mark ? ` (${mark.label})` : ''}${optionTitleSuffix(option)}`
            + (onPick ? ' — click to make this the option Confirm will submit' : '');
    const body = (
        <>
            {mark && <span aria-hidden="true" className="not-italic">{mark.glyph}</span>}
            {failed ? 'not quoted' : (
                fcs.length === 0 ? '—' : (
                    <>
                        {shown.map((fc, i) => (
                            <React.Fragment key={fc}>
                                {i > 0 && ', '}
                                <FcCode code={fc} locations={locations} />
                            </React.Fragment>
                        ))}
                        {overflow > 0 && (
                            <span title={fcs.slice(FC_PER_OPTION_VISIBLE)
                                .map(fc => fcTooltip(fc, locations)).join('\n')}>
                                {` +${overflow}`}
                            </span>
                        )}
                    </>
                )
            )}
        </>
    );
    // Owner, 2026-09-01: "the user should be able to select the FCs option" — a pickable chip is
    // a real button; a display-only chip stays a span so nothing looks clickable that isn't.
    if (onPick) {
        return (
            <button
                type="button"
                onClick={onPick}
                aria-label={`Use option ${index + 1}: ${fcChipText(fcs)}`}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border font-mono text-[10px] whitespace-nowrap cursor-pointer hover:ring-1 hover:ring-indigo-300 ${cls}`}
                title={chipTitle}
                data-testid={`placement-chip-${index}`}
                data-mark={mark ? mark.kind : 'none'}
            >
                {body}
            </button>
        );
    }
    return (
        <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border font-mono text-[10px] whitespace-nowrap cursor-help ${cls}`}
            title={chipTitle}
            data-testid={`placement-chip-${index}`}
            data-mark={mark ? mark.kind : 'none'}
        >
            {body}
        </span>
    );
}

/**
 * Task 103 (owner: "add the FCs in another column on the shipment runs page", then "create a
 * column showing the FCs for each response from amazon. highlight the recommended one") — every
 * placement option Amazon returned, each with its own destination FCs, so the geographic spread on
 * offer is comparable without opening the run.
 *
 * Row height is bounded on purpose: at most {@link FC_OPTIONS_VISIBLE} option chips, each showing
 * at most {@link FC_PER_OPTION_VISIBLE} codes before collapsing to "+N", with the full FC list of
 * every option on hover. A run with 3 options × 5 FCs would be fifteen codes rendered naively; this
 * keeps the table scannable and puts the full matrix one hover (or one "Open") away.
 *
 * A run whose analysis FAILED has no options at all and renders as a failure, never as an empty
 * cell — blank-because-Amazon-refused and blank-because-nothing-quoted must not look alike.
 */
/**
 * Owner, 2026-09-01: "the user should be able to select the FCs option - this selected FC option
 * will be submitted if the user check the row checkbox and submits with 'confirm'". Pure state
 * updater for a pick: re-points ONE run's selected_placement_id and its option flags; every other
 * run keeps its identity (===) so React re-renders only the row that changed. Never mutates input.
 */
export function applyPlacementPick(runs, runId, placementOptionId) {
    return (runs || []).map(run => {
        if (String(run?.id) !== String(runId)) return run;
        return {
            ...run,
            selected_placement_id: placementOptionId,
            placementFcs: (run.placementFcs || []).map(o => ({
                ...o, selected: o.placementOptionId === placementOptionId,
            })),
        };
    });
}

export function DestinationFcsCell({ run, onPickOption }) {
    const locations = useFcLocations();
    if (run?.analysisError) {
        return (
            <span
                className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-semibold whitespace-nowrap cursor-help"
                title={run.analysisError}
                data-testid={`run-fcs-failed-${run.id}`}
            >
                not quoted<span className="ml-1 opacity-70">ⓘ</span>
            </span>
        );
    }

    const options = run?.placementFcs || [];
    // Older runs (analyzed before per-option FCs were recorded) still show the single list.
    if (options.length === 0) {
        const fcs = run?.destinationFcs || [];
        if (fcs.length === 0) return <span className="text-gray-400">—</span>;
        const shown = fcs.slice(0, FC_CELL_VISIBLE);
        const overflow = fcs.length - shown.length;
        return (
            <span className="font-mono text-xs text-gray-700 whitespace-nowrap"
                data-testid={`run-fcs-${run.id}`}>
                {shown.map((fc, i) => (
                    <React.Fragment key={fc}>
                        {i > 0 && ', '}
                        <FcCode code={fc} locations={locations} className="cursor-help" />
                    </React.Fragment>
                ))}
                {overflow > 0 && (
                    <span
                        className="ml-1 px-1 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-semibold cursor-help"
                        title={fcs.slice(FC_CELL_VISIBLE).map(fc => fcTooltip(fc, locations)).join('\n')}
                    >
                        +{overflow}
                    </span>
                )}
            </span>
        );
    }

    const anyConfirmed = options.some(o => o.confirmed);
    // Pickable only where a choice exists and still matters: an ACTIVE run with more than one
    // quotable option and nothing confirmed. A settled or single-option run renders as before.
    const usable = options.filter(o => !o.transportError && o.placementOptionId);
    const pickable = typeof onPickOption === 'function'
        && String(run?.status || '').toUpperCase() === 'ACTIVE'
        && !anyConfirmed && usable.length > 1;
    const visible = options.slice(0, FC_OPTIONS_VISIBLE);
    const hidden = options.length - visible.length;
    return (
        <span className="flex flex-wrap items-center gap-1 max-w-[16rem]" data-testid={`run-fcs-${run.id}`}>
            {visible.map((o, i) => (
                <PlacementChip key={o.placementOptionId || i} option={o}
                    mark={placementMark(o, { anyConfirmed })} index={i} locations={locations}
                    onPick={pickable && !o.transportError && o.placementOptionId
                        ? () => onPickOption(run.id, o.placementOptionId) : undefined} />
            ))}
            {hidden > 0 && (
                <span
                    className="text-[10px] text-gray-500 cursor-help"
                    title={options.slice(FC_OPTIONS_VISIBLE)
                        .map((o, i) => `Option ${FC_OPTIONS_VISIBLE + i + 1}: `
                            + ((o.fcs || []).map(fc => fcTooltip(fc, locations)).join(' | ') || 'no destinations'))
                        .join('\n')}
                >
                    +{hidden} more
                </span>
            )}
        </span>
    );
}

/**
 * Stage 2 of the search (owner: "make the filter server side and allow us to filter on that result
 * set"): narrows the rows ALREADY on screen, instantly and with no round trip. Deliberately the
 * same field set as the server-side search so the two boxes never disagree about what a term means.
 *
 * An empty term returns the input untouched — clearing the refine box restores the full server
 * result set without re-querying.
 */
export function refineRuns(runs, term) {
    const q = String(term || '').trim().toLowerCase();
    if (!q) return runs || [];
    const has = (v) => v != null && String(v).toLowerCase().includes(q);
    return (runs || []).filter(run => (
        has(run.id) || has(run.inbound_plan_id) || has(run.status) || has(run.source_mode)
        || (run.destinationFcs || []).some(has)
        || (run.shipmentNames || []).some(has)
        || (run.shipmentIds || []).some(has)
        || (run.asins || []).some(a => has(a.asin) || has(a.name) || has(a.sku) || has(a.fnsku) || has(a.itemCode))
    ));
}

/**
 * Task 103 (owner: "the 'status' colum should be a selector type filter and by default oonly
 * showing 'active'"). The real run statuses, in the order the control offers them.
 */
export const RUN_STATUSES = ['ACTIVE', 'CONFIRMED', 'CANCELLED', 'SUPERSEDED'];
/** Sentinel for "no status filter at all". */
export const STATUS_ALL = 'ALL';
/** The default on a fresh session: the runs that can still be acted on. */
export const DEFAULT_STATUS_FILTER = 'ACTIVE';
/** sessionStorage, NOT localStorage, on purpose: the choice survives navigating around the app,
 * and a fresh session still opens on ACTIVE as specified. */
export const STATUS_FILTER_STORAGE_KEY = 'shipmentQueueStatusFilter';

export function readStoredStatusFilter(storage) {
    try {
        const raw = (storage || window.sessionStorage).getItem(STATUS_FILTER_STORAGE_KEY);
        if (raw === STATUS_ALL || RUN_STATUSES.includes(raw)) return raw;
    } catch (e) { /* private mode / no storage -- fall through to the default */ }
    return DEFAULT_STATUS_FILTER;
}

export function writeStoredStatusFilter(value, storage) {
    try {
        (storage || window.sessionStorage).setItem(STATUS_FILTER_STORAGE_KEY, value);
    } catch (e) { /* never worth failing a render over */ }
}

/**
 * The sentence under the table. This is the guard against a hiding default becoming a lie, so it
 * is a pure function with its own tests rather than JSX buried in the page.
 *
 * Three things it must never do:
 *  - report a bare "no results" when the status filter is what removed them;
 *  - state a shown-count without the total it came from;
 *  - stay silent about matches the filter excluded.
 *
 * @returns {{text: string, hidden: number, tone: string}}
 */
export function queueCountsLine({ shown, matchCount, hiddenByStatus, statusFilter, searching, truncated, scanned }) {
    const hidden = hiddenByStatus || 0;
    const filtered = statusFilter !== STATUS_ALL;
    const scope = searching
        ? (truncated ? ` (searched the most recent ${scanned} runs)` : ' across all runs')
        : '';
    if (matchCount === 0 && hidden > 0) {
        // The dangerous case, made loud: a search that found something the filter is hiding.
        return {
            hidden,
            tone: 'warn',
            text: `No ${statusFilter.toLowerCase()} runs${searching ? ' match' : ''} — but `
                + `${hidden} ${hidden === 1 ? 'run is' : 'runs are'} hidden by the `
                + `${statusFilter.toLowerCase()} filter.`,
        };
    }
    const base = `Showing ${shown} of ${matchCount}${searching ? ` match${matchCount === 1 ? '' : 'es'}` : ' run' + (matchCount === 1 ? '' : 's')}${scope}`;
    if (hidden > 0) {
        return {
            hidden,
            tone: 'warn',
            text: `${base} · ${hidden} more hidden by the ${statusFilter.toLowerCase()} filter.`,
        };
    }
    return { hidden: 0, tone: 'plain', text: `${base}${filtered ? ` · ${statusFilter.toLowerCase()} only` : ''}.` };
}

// Owner, 2026-09-03: "default should be collapsed, and under the Shipment Queue section" — the
// queue is the page; Recommended / Analysis jobs / Watch list / Partition sweeps are reference
// material BELOW it. Children stay MOUNTED while collapsed (CSS-hidden, never unmounted): every
// section owns its own polling, and collapsing must not silently stop a watch/job/sweep refresh.
// First-ever default is collapsed; each section's expanded choice persists per operator.
const sectionOpenKey = (id) => `runsSectionOpen.${id}`;
function readSectionOpen(id, defaultOpen = false) {
    try {
        const stored = window.localStorage.getItem(sectionOpenKey(id));
        if (stored === '1') return true;
        if (stored === '0') return false;
        return defaultOpen;
    } catch (e) { return defaultOpen; }
}
export function CollapsibleSection({ id, title, count, defaultOpen = false, children }) {
    const [open, setOpen] = useState(() => readSectionOpen(id, defaultOpen));
    const toggle = () => setOpen(prev => {
        const next = !prev;
        try { window.localStorage.setItem(sectionOpenKey(id), next ? '1' : '0'); } catch (e) { /* private mode */ }
        return next;
    });
    return (
        <div className="mt-2">
            <button type="button" data-testid={`section-toggle-${id}`} onClick={toggle}
                    className="w-full flex items-center gap-2 px-1 py-2 text-left text-sm font-semibold text-gray-700 hover:text-gray-900">
                <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>
                {title}
                {count != null && (
                    <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-normal">{count}</span>
                )}
            </button>
            <div data-testid={`section-body-${id}`} className={open ? '' : 'hidden'}>
                {children}
            </div>
        </div>
    );
}

// ---------- Analysis section — Decision Cards (owner, 2026-09-05) ----------
//
// "we are still struggling with understanding which shipments option are the correct option to
// use - we need to distill down further." One card per product with something to decide; the
// Shipment Queue below stays untouched as the reference layer (owner scope change: "add this a
// 'Analysis' Section and leave the shipment queue as is for reference").

function DecisionFcChips({ fcs, newFcs }) {
    const fresh = new Set(newFcs || []);
    return (
        <span>
            {(fcs || []).map(fc => fresh.has(fc) ? (
                <span key={fc} data-testid={`decision-fc-new-${fc}`}
                      title={`${fc} — new, not currently covered`}
                      className="inline-block px-1.5 py-0.5 mr-1 mb-1 text-xs font-mono rounded border bg-emerald-100 text-emerald-800 border-emerald-300">
                    {fc}
                </span>
            ) : (
                <span key={fc} data-testid={`decision-fc-covered-${fc}`}
                      title={`${fc} — already covered by current stock`}
                      className="inline-block px-1.5 py-0.5 mr-1 mb-1 text-xs font-mono rounded border bg-gray-100 text-gray-500 border-gray-200">
                    {fc}
                </span>
            ))}
        </span>
    );
}

function decisionBoxLine(box) {
    if (!box || !box.boxes || !box.unitsPerBox) return null;
    let line = `${box.boxes} box${box.boxes !== 1 ? 'es' : ''} × ${box.unitsPerBox}/box`;
    if (box.dims) line += ` · ${box.dims}`;
    if (box.weightLbs != null) line += ` · ${box.weightLbs} lb`;
    return line;
}

/** One option (recommended or alternative) rendered with its full evidence + its own confirm. */
function DecisionOption({ option, primary, onConfirm, confirming }) {
    const boxLine = decisionBoxLine(option.boxConfig);
    return (
        <div className={primary ? '' : 'mt-2 pt-2 border-t border-gray-100'}>
            <div className="flex flex-wrap items-center gap-2">
                <span className={`font-medium ${primary ? 'text-gray-900' : 'text-gray-700 text-sm'}`}>
                    {option.action}
                </span>
                {option.decision && <DecisionPill decision={option.decision} />}
                {typeof option.score === 'number' && (
                    <span className="text-xs text-emerald-700 font-medium">+{option.score.toFixed(1)}pp</span>
                )}
                {option.unpriced && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                        unpriced — measure pallets to score
                    </span>
                )}
            </div>
            <ul className="mt-1 text-xs text-gray-600 space-y-0.5">
                {(option.why || []).map((w, i) => <li key={i}>· {w}</li>)}
            </ul>
            <div className="mt-1">
                <DecisionFcChips fcs={option.fcs} newFcs={option.newFcs} />
            </div>
            {boxLine && <div className="text-xs text-gray-500 font-mono">{boxLine}</div>}
            <div className="mt-2 flex items-center gap-3">
                {option.unpriced ? (
                    <button disabled
                            className="px-3 py-1.5 text-sm rounded-md bg-gray-200 text-gray-500 cursor-not-allowed"
                            title="Phase-1 LTL — freight is only priced from measured pallets">
                        Measure pallets to confirm
                    </button>
                ) : (
                    <button onClick={onConfirm} disabled={confirming}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium ${primary
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'} disabled:opacity-50`}>
                        {confirming ? 'Confirming one at a time…'
                            : primary ? `Confirm recommended (${option.runIds.length === 1 ? `run ${option.runId}` : `${option.runIds.length} plans`})`
                            : 'Confirm this instead'}
                    </button>
                )}
            </div>
        </div>
    );
}

/** Selections for the batch endpoint — FULL uses the option itself, SPLIT its members. */
export function decisionSelections(option) {
    if (option.members && option.members.length) {
        return option.members.map(m => ({
            runId: m.runId, planId: m.planId, placementOptionId: m.placementOptionId,
        }));
    }
    return [{ runId: option.runId, planId: option.planId, placementOptionId: option.placementOptionId }];
}

function DecisionCard({ card, onConfirmed }) {
    const [confirming, setConfirming] = useState(false);
    const [outcomes, setOutcomes] = useState(null);
    const [showAlts, setShowAlts] = useState(false);

    const urgency = card.runway?.urgency;
    const urgencyCls = urgency === 'CRITICAL' ? 'bg-red-100 text-red-800'
        : urgency === 'LOW' ? 'bg-amber-100 text-amber-800'
        : urgency === 'HEALTHY' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600';

    const confirmOption = async (option) => {
        const selections = decisionSelections(option);
        if (!window.confirm(batchConfirmPrompt(selections.length))) return;
        setConfirming(true);
        setOutcomes(null);
        try {
            await postAndPollBatch(selections, setOutcomes);
            if (onConfirmed) onConfirmed();
        } catch (err) {
            setOutcomes({ status: 'FAILED', results: [],
                error: err.response?.data?.error || err.message });
        }
        setConfirming(false);
    };

    return (
        <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white">
            <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-gray-900">
                    {card.productName || card.asin}
                </span>
                {card.itemCode && <span className="text-xs font-mono text-gray-500">{card.itemCode}</span>}
                <span className="text-xs font-mono text-gray-400">{card.asin}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${urgencyCls}`}>
                    {urgency === 'UNKNOWN' ? 'no recent sales' : (urgency || '').toLowerCase()}
                </span>
                <Link to={`/shipment-plans?asin=${card.asin}`}
                      className="ml-auto text-xs text-indigo-600 hover:text-indigo-800">
                    open workspace →
                </Link>
            </div>

            <div className="mt-2">
                <DecisionOption option={card.recommended} primary
                                onConfirm={() => confirmOption(card.recommended)}
                                confirming={confirming} />
            </div>

            {(card.alternatives || []).length > 0 && (
                <div className="mt-2">
                    <button onClick={() => setShowAlts(v => !v)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 underline">
                        {showAlts ? 'Hide' : 'Show'} {card.alternatives.length} alternative{card.alternatives.length !== 1 ? 's' : ''}
                    </button>
                    {showAlts && card.alternatives.map(alt => (
                        <DecisionOption key={alt.runId} option={alt}
                                        onConfirm={() => confirmOption(alt)}
                                        confirming={confirming} />
                    ))}
                </div>
            )}

            {card.historyCount > 0 && (
                <div className="mt-2 text-xs text-gray-400">
                    {card.historyCount} older draws in the queue below — evidence, not choices.
                </div>
            )}

            {outcomes && (
                <div className="mt-2 text-xs space-y-0.5">
                    {outcomes.error && <div className="text-red-700">{outcomes.error}</div>}
                    {(outcomes.results || []).map(r => (
                        <div key={r.runId}
                             className={r.outcome === 'CONFIRMED' ? 'text-emerald-800' : 'text-red-700'}>
                            Run #{r.runId} — {r.outcome}{r.message ? `: ${r.message}` : ''}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function AnalysisSection({ onCountChange }) {
    const [cards, setCards] = useState([]);
    const [error, setError] = useState(null);
    const countRef = useRef(onCountChange);
    countRef.current = onCountChange;

    const load = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/decisions');
            const list = res.data?.decisions || [];
            setCards(list);
            setError(null);
            if (countRef.current) countRef.current(list.length);
        } catch (err) {
            // The decision layer degrades to its error line — the queue below is unaffected.
            setError(err.response?.data?.error || err.message);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3">
            {error && <div className="text-sm text-red-600">Couldn&apos;t build decisions: {error}</div>}
            {!error && cards.length === 0 && (
                <div className="text-sm text-gray-500 px-1 py-2">
                    No products need a decision right now — nothing confirmable is waiting.
                </div>
            )}
            {cards.map(card => (
                <DecisionCard key={card.asin} card={card} onConfirmed={load} />
            ))}
        </div>
    );
}

// Owner, 2026-09-03 ("add a button on each row of the probe to add to the auto queue that will
// contiune checking the shipments every 6 hrs"): a probe row is promotable onto the background
// watch list. It reads as already-watched only when EVERY item of its composition has an ACTIVE
// watch row at the SAME quantity — a different qty is a different configuration, the same rule
// the planner's own watch button applies.
/** Dual cadence 2026-09-04: "5x10 split · every 12h" / "50 units · every 6h" -- each watch
 * row's shape and cadence, spelled out on the Watch list. */
export function watchRowShapeLabel(row) {
    const interval = row?.drawIntervalHours || 6;
    return row?.chunkSpec
        ? `${row.chunkSpec} split · every ${interval}h`
        : `${row?.qty} units · every ${interval}h`;
}

export function runFullyWatched(run, watchRows) {
    const items = (run?.asins || []).filter(a => a && a.asin);
    if (items.length === 0) return false;
    const active = new Map();
    (watchRows || []).forEach(r => {
        // Dual cadence 2026-09-04: split-shape rows (chunkSpec) never count as full coverage;
        // neither do SPREAD rows (FC Spread Target, V2 -- their own shape even with no chunkSpec).
        if (r && String(r.status).toUpperCase() === 'ACTIVE' && r.asin && !r.chunkSpec && r.purpose !== 'SPREAD') {
            active.set(r.asin, r.qty);
        }
    });
    return items.every(i => active.has(i.asin) && Number(active.get(i.asin)) === Number(i.qty));
}

export default function ShipmentRuns() {
    const navigate = useNavigate();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortField, setSortField] = useState('date');
    const [sortDir, setSortDir] = useState('desc'); // newest first by default
    const [cancellingId, setCancellingId] = useState(null);
    // Task 40: cancel-confirmed-shipment dialog -- holds the run being cancelled, or null when closed.
    const [cancelTarget, setCancelTarget] = useState(null);

    // Owner correction 2026-09-01: batch confirm on the Shipment Queue itself (the board kept
    // its own). Selection keyed by run id; the confirms are the SAME one-at-a-time server-side
    // batch, shared machinery (batchConfirmPrompt / postAndPollBatch / BatchConfirmBar).
    const [queueBatchSel, setQueueBatchSel] = useState(() => new Map());
    // Owner, 2026-09-02: FC map on row hover. The delay filters drive-by mouse transit; the
    // popover is pinned to the row (under its FC cell), never cursor-following.
    const [hoverMapRunId, setHoverMapRunId] = useState(null);
    const hoverMapTimerRef = useRef(null);
    const openMapSoon = useCallback((id) => {
        clearTimeout(hoverMapTimerRef.current);
        hoverMapTimerRef.current = setTimeout(() => setHoverMapRunId(id), 250);
    }, []);
    const closeMap = useCallback(() => {
        clearTimeout(hoverMapTimerRef.current);
        setHoverMapRunId(null);
    }, []);
    useEffect(() => () => clearTimeout(hoverMapTimerRef.current), []);
    const [queueBatchRunning, setQueueBatchRunning] = useState(false);
    const [queueBatchOutcomes, setQueueBatchOutcomes] = useState(null);
    const toggleQueueBatchRow = useCallback((run) => {
        setQueueBatchSel(prev => {
            const next = new Map(prev);
            if (next.has(run.id)) next.delete(run.id); else next.set(run.id, run);
            return next;
        });
    }, []);

    // Owner, 2026-09-01: "the user should be able to select the FCs option - this selected FC
    // option will be submitted if the user check the row checkbox and submits with 'confirm'".
    // The pick re-points the row immediately (the batch payload follows via queueBatchSelection's
    // selected-wins rule), updates any already-checked snapshot of the same row so check-then-pick
    // and pick-then-check submit the same thing, and persists fire-and-forget through the same
    // endpoint the planner page uses -- a lost write degrades to the recommendation, never blocks.
    const pickPlacementOption = useCallback((runId, placementOptionId) => {
        setRuns(prev => applyPlacementPick(prev, runId, placementOptionId));
        setQueueBatchSel(prev => {
            let touched = false;
            const next = new Map(prev);
            for (const [k, v] of next) {
                if (String(k) === String(runId)) {
                    next.set(k, applyPlacementPick([v], runId, placementOptionId)[0]);
                    touched = true;
                }
            }
            return touched ? next : prev;
        });
        ApiClient.post(`/api/shipment-planner/runs/${runId}/selected-placement`, { placementOptionId })
            .catch(err => console.error('Failed to persist placement pick:', err));
    }, []);

    // Task 43 (live updates): background polls skip the loading flag so the table never flickers --
    // state updates and React reconciles in place; sort/expanded state lives in this component and
    // is untouched by a refresh.
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [pollMs, setPollMs] = useState(POLL_MS_FALLBACK);
    // Task 80: ACTIVE runs sitting on used-condition SKUs, badged in the runs table below.
    const usedSkuAlerts = useUsedSkuAlerts(pollMs);

    // 2026-09-03 collapse work: each section reports its count so the collapsed headers stay
    // informative. One state object, one stable callback per section.
    const [sectionCounts, setSectionCounts] = useState({});
    const countRecommended = useCallback(n => setSectionCounts(p => (p.recommended === n ? p : { ...p, recommended: n })), []);
    const countJobs = useCallback(n => setSectionCounts(p => (p.jobs === n ? p : { ...p, jobs: n })), []);
    const countAnalysis = useCallback(n => setSectionCounts(p => (p.analysis === n ? p : { ...p, analysis: n })), []);
    const countWatch = useCallback(n => setSectionCounts(p => (p.watch === n ? p : { ...p, watch: n })), []);
    const countSweeps = useCallback(n => setSectionCounts(p => (p.sweeps === n ? p : { ...p, sweeps: n })), []);

    // Owner, 2026-09-03: probe rows can join the auto queue. The ACTIVE watch rows tell the
    // button whether the composition is already queued; indication only — a failed read leaves
    // the button usable (the server just upserts the same row).
    const [watchRows, setWatchRows] = useState([]);
    const [probeWatchBusy, setProbeWatchBusy] = useState(null);
    const watchProbeRun = useCallback(async (run) => {
        setProbeWatchBusy(run.id);
        try {
            await ApiClient.post('/api/shipment-planner/watch', {
                source: `probe run ${run.id}`,
                // Probes are UPS-only today; a Phase-1 LTL run would carry AWAITING_PALLETS.
                shipmentMode: run.planner_phase === 'AWAITING_PALLETS' ? 'LTL' : 'UPS',
                items: (run.asins || []).filter(a => a && a.asin).map(a => ({ asin: a.asin, qty: a.qty })),
            });
            toast.success('Watching — drawing every 6 hours until confirmed or stopped. Track it on the Watch list.');
            // Optimistic flip to "Watching ✓"; the next watch poll confirms it.
            setWatchRows(prev => [...prev,
                ...(run.asins || []).filter(a => a && a.asin).map(a => ({ asin: a.asin, qty: a.qty, status: 'ACTIVE' }))]);
        } catch (err) {
            toast.error('Watch failed: ' + (err.response?.data?.error || err.message));
        }
        setProbeWatchBusy(null);
    }, []);

    // Task 103 (owner: "also add a search bar" / "make the filter server side and allow us to
    // filter on that result set"). TWO stages, deliberately distinct:
    //   `search` hits the server across ALL runs -- so a term that only matches a run older than
    //           this page's window is still found. That is the whole reason it is server-side.
    //   `refine` narrows what came back, instantly, with no round trip. Clearing it restores the
    //           full server result set without re-querying.
    const [search, setSearch] = useState('');
    const [refine, setRefine] = useState('');
    const [searchMeta, setSearchMeta] = useState(null);
    const searchRef = useRef('');
    searchRef.current = search;

    // Task 103 (owner: "the 'status' colum should be a selector type filter and by default oonly
    // showing 'active'"). Applied SERVER-side alongside the search so the counts describe reality
    // rather than a page-local slice -- see queueCountsLine for why the counts are the whole point.
    const [statusFilter, setStatusFilter] = useState(() => readStoredStatusFilter());
    const statusRef = useRef(statusFilter);
    statusRef.current = statusFilter;

    const load = useCallback(async ({ background = false } = {}) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        const q = searchRef.current.trim();
        const status = statusRef.current;
        try {
            // ONE endpoint for both reads now. The status filter HIDES rows by default, so the
            // shown/total/hidden counts have to come from the same server-side pass that did the
            // hiding -- deriving them from whatever the page happens to hold would be exactly the
            // page-local half-truth this is meant to prevent.
            const params = new URLSearchParams({ limit: '200' });
            if (q) params.set('q', q);
            if (status !== STATUS_ALL) params.set('status', status);
            const res = await ApiClient.get(`/api/shipment-planner/runs-search?${params.toString()}`);
            setRuns(res.data?.matches || []);
            // Probe-row watch state (owner 2026-09-03) — non-fatal, indication only.
            try {
                const w = await ApiClient.get('/api/shipment-planner/watch');
                setWatchRows(Array.isArray(w.data) ? w.data : []);
            } catch (watchErr) { /* button stays usable; server upsert is idempotent */ }
            // (pickPlacementOption below re-points rows in this same state on a pick.)
            setSearchMeta({
                matchCount: res.data?.matchCount ?? 0,
                hiddenByStatus: res.data?.hiddenByStatus ?? 0,
                statusCounts: res.data?.statusCounts || {},
                totalCount: res.data?.totalCount ?? 0,
                scanned: res.data?.scanned ?? 0,
                truncated: !!res.data?.truncated,
                searching: !!q,
            });
            setError(null);
            setLastUpdatedAt(Date.now());
        } catch (err) {
            console.error('Failed to load shipment runs:', err);
            if (!background) {
                setError(err.response?.data?.error || err.message || 'Failed to load runs');
            }
        }
        if (!background) setLoading(false);
    }, []);

    // Debounced server read: re-runs whenever the search term settles OR the status filter changes.
    useEffect(() => {
        const id = setTimeout(() => { load({ background: true }); }, 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter]);

    const changeStatusFilter = useCallback((next) => {
        setStatusFilter(next);
        writeStoredStatusFilter(next);
    }, []);

    // Adaptive cadence off GET /api/shipment-planner/background-busy (Task 42's deploy-guard
    // endpoint): a running watch/probe cycle means new runs are landing -- poll fast; idle means
    // relax. Endpoint unreachable -> plain 20s.
    const refreshPollCadence = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/background-busy');
            setPollMs(res.data && res.data.busy ? POLL_MS_BUSY : POLL_MS_IDLE);
        } catch (err) {
            setPollMs(POLL_MS_FALLBACK);
        }
    }, []);

    useEffect(() => {
        load();
        refreshPollCadence();
    }, [load, refreshPollCadence]);

    const backgroundRefresh = useCallback(() => {
        load({ background: true });
        refreshPollCadence();
    }, [load, refreshPollCadence]);
    useVisiblePolling(backgroundRefresh, pollMs);

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir(field === 'date' ? 'desc' : 'asc');
        }
    };

    // Counts for the selector's own option labels, straight from the server's breakdown over the
    // matched set -- so "Confirmed (30)" tells the operator where their run went before they even
    // change the filter.
    const statusTotals = useMemo(() => {
        if (!searchMeta) return null;
        const byStatus = searchMeta.statusCounts || {};
        return { byStatus, all: searchMeta.totalCount ?? 0 };
    }, [searchMeta]);

    // Stage 2 runs BEFORE the sort so the table sorts what is actually shown.
    const visibleRuns = useMemo(() => refineRuns(runs, refine), [runs, refine]);

    const sortedRuns = useMemo(() => {
        const keyFn = SORT_FIELDS[sortField] || SORT_FIELDS.date;
        const copy = [...visibleRuns];
        copy.sort((a, b) => {
            const av = keyFn(a);
            const bv = keyFn(b);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return copy;
    }, [visibleRuns, sortField, sortDir]);

    // Owner, 2026-09-01: same-composition runs cluster together (groups form over the sorted
    // rows, so grouping never fights the chosen sort).
    const groupedRuns = useMemo(() => groupRunsByComposition(sortedRuns), [sortedRuns]);

    const cancelPlan = async (run) => {
        if (!run.inbound_plan_id) return;
        if (!window.confirm(`Cancel this draft plan (Run #${run.id}) on Amazon?`)) return;
        setCancellingId(run.id);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${run.inbound_plan_id}`);
            await load();
        } catch (err) {
            console.error('Cancel failed:', err);
            alert('Cancel failed: ' + (err.response?.data?.error || err.message));
        }
        setCancellingId(null);
    };

    const sortArrow = (field) => {
        if (sortField !== field) return null;
        return <span className="text-gray-400">{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    return (
        // Task 86 (owner: "a scroll was needed - adjust the page format so no scroll is needed"):
        // this page used to be wrapped in the same "max-w-7xl mx-auto" + "px-4 sm:px-6 lg:px-8"
        // pattern as narrow, prose-shaped pages -- but SidebarLayout.js ALREADY wraps every routed
        // page in its own "px-4 sm:px-6 lg:px-8" (see SidebarLayout.js's <main>), so that repeated
        // padding was doubled, and the max-w-7xl (1280px) cap threw away everything a wide monitor
        // offered on top of that. This is a data-dense, table-heavy page (Recommended board with
        // 9 columns, the runs log, watch list) -- it gets the full width SidebarLayout leaves after
        // the sidebar (lg:pl-72) and its own single padding pass, growing with the viewport instead
        // of freezing at 1280px.
        <div className="py-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <ClockIcon className="h-8 w-8 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Shipment Runs</h1>
                        <p className="text-sm text-gray-500">
                            Every Shipment Planner analysis, across both workflows — PO mode and ASIN list mode.
                        </p>
                        {/* Task 97: a split's siblings are scattered down this log by analyze time;
                            the assessment page puts one set's shipments side by side. */}
                        <a href="/split-sets"
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                            data-testid="runs-split-sets-link">
                            Assess split sets →
                        </a>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <UpdatedAgo timestamp={lastUpdatedAt} />
                    <button
                        onClick={() => load()}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Owner, 2026-09-05: the Analysis section is the page's primary surface — one
                Decision Card per product that has something to decide, default EXPANDED; the
                Shipment Queue below stays exactly as-is, the full reference layer. */}
            {/* Owner, 2026-09-06: titled "Decisions" — the original "Analysis" title collided
                with "Analysis jobs" below and the owner opened the wrong one looking for the
                distillation. This section answers "what do I do"; jobs answers "what ran". */}
            <CollapsibleSection id="analysis" title="Decisions" count={sectionCounts.analysis} defaultOpen>
                <AnalysisSection onCountChange={countAnalysis} />
            </CollapsibleSection>

            {/* Task 103, owner verbatim: "add a lable to the bottome table - shipment Queue".
                Owner, 2026-09-03: the queue leads the page now — Recommended / Analysis jobs /
                Watch list / Partition sweeps render BELOW it, collapsed by default. */}
            <h2 className="text-lg font-semibold text-gray-900 mb-2 mt-4" data-testid="shipment-queue-heading">
                Shipment Queue
            </h2>

            {/* Owner correction 2026-09-01: the line-by-line batch confirm lives HERE too. */}
            <BatchConfirmBar
                count={queueBatchSel.size}
                running={queueBatchRunning}
                outcomes={queueBatchOutcomes}
                onStart={async () => {
                    const picked = Array.from(queueBatchSel.values()).filter(r => queueBatchEligible(r));
                    if (picked.length === 0) return;
                    if (!window.confirm(batchConfirmPrompt(picked.length))) return;
                    setQueueBatchRunning(true);
                    setQueueBatchOutcomes(null);
                    try {
                        await postAndPollBatch(picked.map(queueBatchSelection), setQueueBatchOutcomes);
                        setQueueBatchSel(new Map());
                        await load({ background: true });
                    } catch (err) {
                        console.error('Queue batch confirm failed to start:', err);
                        toast.error('Batch confirm failed to start: '
                            + (err.response?.data?.error || err.message));
                    }
                    setQueueBatchRunning(false);
                }}
                onClear={() => setQueueBatchSel(new Map())}
                onDismiss={() => setQueueBatchOutcomes(null)}
            />

            {/* Mode legend */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-3">
                <span className="flex items-center gap-1"><ModeBadge mode="PO" /> Shipment Planner, PO mode</span>
                <span className="flex items-center gap-1"><ModeBadge mode="ASIN" /> Shipment Planner, ASIN list mode</span>
                <span className="flex items-center gap-1"><ModeBadge mode="PROBE" /> 6-hourly placement probe (analyzed &amp; always voided)</span>
                <span className="flex items-center gap-1"><ModeBadge mode="WATCH" /> Background watch draw (real plan, left unconfirmed)</span>
                <span className="flex items-center gap-1"><ModeBadge mode="SWEEP" /> Partition sweep chunk (real plan, left unconfirmed)</span>
                <span className="flex items-center gap-1"><ModeBadge mode={null} /> Legacy run, mode not recorded</span>
            </div>

            {loading && runs.length === 0 && <div className="text-gray-500">Loading…</div>}
            {error && <div className="text-red-600 mb-4">Error: {error}</div>}

            {/* Task 103 — the two-stage search. Owner: "also add a search bar", then "make the
                filter server side and allow us to filter on that result set". The two boxes are
                deliberately unalike: the primary one is wide, icon-led and styled like the
                planner's own search bar (so the two pages feel like one product), and it queries
                the WHOLE history; the refine box is small, secondary, and only narrows what is
                already on screen. */}
            <div className="mb-3 flex flex-wrap items-end gap-3">
                <div className="relative flex-1 min-w-[20rem] max-w-2xl">
                    <label htmlFor="runs-search" className="block text-sm font-medium text-gray-900 mb-1">
                        Search all runs
                    </label>
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-[2.15rem] pointer-events-none" />
                    <input
                        id="runs-search"
                        type="text"
                        autoComplete="off"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="ASIN, item code, SKU, FNSKU, run #, plan ID, FBA ID, PO, shipment name, FC"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pl-10 pr-3 py-2.5"
                    />
                </div>
                <div className="w-56">
                    <label htmlFor="runs-refine" className="block text-xs font-medium text-gray-500 mb-1">
                        Refine these results
                    </label>
                    <input
                        id="runs-refine"
                        type="text"
                        autoComplete="off"
                        value={refine}
                        onChange={e => setRefine(e.target.value)}
                        placeholder="narrow what's shown"
                        className="block w-full rounded-md border-gray-200 bg-gray-50 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 text-xs px-3 py-2"
                    />
                </div>
            </div>

            {/* Task 103 — the guard against a hiding default. A filter that removes rows is a
                silent-omission risk, so the applied filter, the shown count, the true total AND
                the number the filter is hiding are all stated in one line, with a one-click way
                out. An operator must never wonder where their confirmed run went. */}
            {searchMeta && (() => {
                const line = queueCountsLine({
                    shown: sortedRuns.length,
                    matchCount: searchMeta.matchCount,
                    hiddenByStatus: searchMeta.hiddenByStatus,
                    statusFilter,
                    searching: searchMeta.searching,
                    truncated: searchMeta.truncated,
                    scanned: searchMeta.scanned,
                });
                return (
                    <div
                        className={`mb-3 text-xs rounded px-3 py-2 border ${line.tone === 'warn'
                            ? 'text-amber-900 bg-amber-50 border-amber-300'
                            : 'text-gray-500 bg-transparent border-transparent px-0 py-0'}`}
                        data-testid="runs-search-meta"
                    >
                        {line.text}
                        {refine && <span> · refined to {sortedRuns.length}.</span>}
                        {line.hidden > 0 && (
                            <button
                                onClick={() => changeStatusFilter(STATUS_ALL)}
                                className="ml-2 font-semibold text-indigo-700 hover:text-indigo-900 underline"
                                data-testid="show-all-statuses"
                            >
                                Show all {searchMeta.totalCount}
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Only when the term genuinely matches nothing. When the STATUS FILTER is what
                emptied the table, the counts line above already says so and offers the way out --
                printing "no runs match" underneath it would contradict it. */}
            {!error && !loading && sortedRuns.length === 0 && (search || refine)
                && !(searchMeta?.hiddenByStatus > 0) && (
                <div className="mb-3 text-sm text-gray-500" data-testid="runs-search-empty">
                    No runs match “{refine || search}”.
                    {refine && ' Clear the refine box to see the full search result.'}
                </div>
            )}

            {!error && (
                <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {/* Owner 2026-09-01: batch selection. Only rows queueBatchEligible
                                    can be picked -- confirmed/cancelled, probe, unpriced-LTL and
                                    expired rows get no checkbox at all. */}
                                <th className="px-2 py-2" />
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Run #</th>
                                <th
                                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer select-none"
                                    onClick={() => toggleSort('date')}
                                >
                                    Created {sortArrow('date')}
                                </th>
                                <th
                                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer select-none"
                                    onClick={() => toggleSort('mode')}
                                >
                                    Mode {sortArrow('mode')}
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Plan ID</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Products</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Items / Units</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">FCs</th>
                                {/* Owner, 2026-09-02: a dedicated column — supersedes the links
                                    that briefly lived inside the Products cell. */}
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">View placement plans</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Shipping</th>
                                {/* The filter lives IN the column header it filters, and always
                                    displays the value in force -- an operator must never have to
                                    look elsewhere to find out why a row is missing. Sorting moves
                                    to the label so the select still behaves like a select. */}
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                                    <span className="cursor-pointer select-none" onClick={() => toggleSort('status')}>
                                        Status {sortArrow('status')}
                                    </span>
                                    <select
                                        aria-label="Filter by status"
                                        data-testid="status-filter"
                                        value={statusFilter}
                                        onChange={e => changeStatusFilter(e.target.value)}
                                        className={`mt-1 block w-full rounded border text-[11px] py-1 pl-1.5 pr-6 font-normal
                                            ${statusFilter !== STATUS_ALL
                                                ? 'border-indigo-400 bg-indigo-50 text-indigo-900 font-semibold'
                                                : 'border-gray-300 bg-white text-gray-700'}`}
                                    >
                                        <option value={STATUS_ALL}>
                                            All statuses{statusTotals ? ` (${statusTotals.all})` : ''}
                                        </option>
                                        {RUN_STATUSES.map(st => (
                                            <option key={st} value={st}>
                                                {st.charAt(0) + st.slice(1).toLowerCase()}
                                                {statusTotals ? ` (${statusTotals.byStatus[st] || 0})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                                <th className="sticky right-0 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 border-l border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {groupedRuns.map(group => (
                                <React.Fragment key={group.key}>
                                    {/* Owner, 2026-09-01: a composition drawn more than once gets a
                                        header so its re-draws read as one decision. Singletons stay
                                        plain rows -- a header per row would just double the table. */}
                                    {group.runs.length > 1 && (
                                        <tr data-testid="queue-group-header" className="bg-indigo-50/60">
                                            <td colSpan={12} className="px-3 py-1.5 text-xs font-medium text-indigo-900 border-t border-indigo-100">
                                                {group.runs[0].asins?.length === 1 ? (
                                                    <>
                                                        {group.runs[0].asins[0].itemCode && (
                                                            <span className="font-mono">{group.runs[0].asins[0].itemCode}</span>
                                                        )}
                                                        {group.runs[0].asins[0].itemCode && ' — '}
                                                        {group.runs[0].asins[0].name || group.runs[0].asins[0].asin}
                                                        {' '}× {group.runs[0].asins[0].qty ?? '?'}
                                                    </>
                                                ) : (
                                                    <>Mixed: {group.runs[0].asins?.length ?? 0} products · {group.runs[0].total_units ?? '?'} units</>
                                                )}
                                                <span className="ml-2 text-indigo-400 font-normal">{group.runs.length} runs</span>
                                            </td>
                                        </tr>
                                    )}
                                    {group.runs.map(run => (
                                <tr key={run.id}
                                    data-testid={`queue-row-${run.id}`}
                                    onMouseEnter={() => openMapSoon(run.id)}
                                    onMouseLeave={closeMap}
                                    onFocus={() => openMapSoon(run.id)}
                                    onBlur={closeMap}
                                    className={`group ${queueBatchSel.has(run.id)
                                        ? 'bg-emerald-50 hover:bg-emerald-100/60'
                                        : 'hover:bg-gray-50'}`}>
                                    <td className={`px-2 py-2 ${queueBatchSel.has(run.id) ? 'border-l-4 border-l-emerald-500' : ''}`}>
                                        {queueBatchEligible(run) && (
                                            <input
                                                type="checkbox"
                                                aria-label="Select for batch confirm"
                                                checked={queueBatchSel.has(run.id)}
                                                onChange={() => toggleQueueBatchRow(run)}
                                                className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                            />
                                        )}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-gray-500">#{run.id}</td>
                                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatDateTime(run.created_at)}</td>
                                    <td className="px-3 py-2"><ModeBadge mode={run.source_mode} /></td>
                                    <td className="px-3 py-2 font-mono text-xs text-gray-600" title={run.inbound_plan_id || ''}>
                                        {truncate(run.inbound_plan_id, 16) || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <ProductsCell asins={run.asins} />
                                        {/* Task 80: ACTIVE runs whose SKUs are not New. Nothing is
                                            edited on these — the fix is to re-create the plan, which
                                            now resolves the New-condition SKU. */}
                                        {usedSkuAlerts.get(String(run.id)) && (
                                            <div className="mt-1"><UsedSkuBadge alerts={usedSkuAlerts.get(String(run.id))} /></div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                        {run.total_items ?? '—'} / {run.total_units ?? '—'}
                                    </td>
                                    <td className="px-3 py-2 relative">
                                        <DestinationFcsCell run={run} onPickOption={pickPlacementOption} />
                                        {hoverMapRunId === run.id && <QueueFcMapPopover run={run} />}
                                    </td>
                                    <td className="px-3 py-2">
                                        {/* Owner, 2026-09-02: single rows link per ASIN; a mixed
                                            row links ONCE to the workspace's composition mode
                                            (?asins=) — "the placement plan page should be looking
                                            at just those skus ... decide on a shipping plan level".
                                            The server-resolved originating PO rides as fromPo so
                                            "Back to PO open lines" works; unresolved → omitted,
                                            never guessed. */}
                                        {(() => {
                                            const distinct = [...new Set((run.asins || []).map(a => a.asin).filter(Boolean))];
                                            const fromPoQ = run.sourcePo ? `&fromPo=${encodeURIComponent(run.sourcePo)}` : '';
                                            if (distinct.length === 0) return null;
                                            if (distinct.length === 1) {
                                                return (
                                                    <Link
                                                        to={`/shipment-plans?asin=${distinct[0]}${fromPoQ}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="View placement plans for this ASIN"
                                                        className="block font-sans text-[11px] text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                                                    >
                                                        view placement plans →
                                                    </Link>
                                                );
                                            }
                                            return (
                                                <Link
                                                    to={`/shipment-plans?asins=${encodeURIComponent(distinct.join(','))}${fromPoQ}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title="View placement plans for this shipping plan"
                                                    className="block font-sans text-[11px] text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                                                >
                                                    view placement plans ({distinct.length} products) →
                                                </Link>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-3 py-2"><ShippingMethodChip method={run.shippingMethod} /></td>
                                    <td className="px-3 py-2"><StatusPill status={run.status} /><PhaseChip phase={run.planner_phase} /></td>
                                    <td className={`sticky right-0 px-3 py-2 border-l border-gray-100 ${queueBatchSel.has(run.id)
                                            ? 'bg-emerald-50 group-hover:bg-emerald-100/60'
                                            : 'bg-white group-hover:bg-gray-50'}`}>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => navigate(`/shipment-planner?run=${run.id}`)}
                                                className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                            >
                                                Open
                                            </button>
                                            {/* Owner, 2026-09-03: a probe row can join the auto
                                                queue — 6-hourly draws until confirmed/stopped.
                                                Already-queued compositions read Watching ✓ so a
                                                double-add is impossible. */}
                                            {run.source_mode === 'PROBE' && (
                                                runFullyWatched(run, watchRows) ? (
                                                    <button disabled
                                                            className="text-xs text-purple-400 cursor-not-allowed"
                                                            title="This composition is already in the 6-hourly auto queue — see the Watch list">
                                                        Watching ✓
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => watchProbeRun(run)}
                                                        disabled={probeWatchBusy === run.id}
                                                        className="text-xs text-purple-600 hover:text-purple-800 hover:underline disabled:opacity-50"
                                                        title="Add to the auto queue — re-draws this composition every 6 hours until its shipment is confirmed or the watch is stopped"
                                                    >
                                                        {probeWatchBusy === run.id ? 'Adding…' : 'Watch every 6h'}
                                                    </button>
                                                )
                                            )}
                                            {String(run.status).toUpperCase() === 'ACTIVE' && run.inbound_plan_id && (
                                                <button
                                                    onClick={() => cancelPlan(run)}
                                                    disabled={cancellingId === run.id}
                                                    className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                                                >
                                                    {cancellingId === run.id ? 'Cancelling…' : 'Cancel plan'}
                                                </button>
                                            )}
                                            {/* Task 40 (owner requirement: "we should be able to cancel via the UI") --
                                                CONFIRMED runs voided a real accepted shipment, not a draft, so this
                                                goes through the deliberate CancelConfirmedShipmentModal instead of the
                                                light window.confirm() used for ACTIVE plans above. */}
                                            {String(run.status).toUpperCase() === 'CONFIRMED' && run.inbound_plan_id && (
                                                <>
                                                    {/* Task 49 (owner, re: Runs page screenshot of run #115 CONFIRMED with
                                                        only Open/Cancel-shipment actions): print belongs everywhere a
                                                        confirmed run appears, not only on the dedicated Confirmed
                                                        Shipments page. */}
                                                    <PrintLabelsAction runId={run.id} planId={run.inbound_plan_id} />
                                                    <button
                                                        onClick={() => setCancelTarget(run)}
                                                        className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                                    >
                                                        Cancel shipment
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                            {!loading && sortedRuns.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-3 py-8 text-center text-gray-400">
                                        No shipment planner runs yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Owner, 2026-09-03: the four reference sections live UNDER the queue, collapsed by
                default (persisted per operator). Task 76's board-first rationale is superseded:
                the queue now answers "what should I ship" via its own verdicts, batch confirm and
                FC pickers, so the board joins the reference material. Children stay mounted while
                collapsed — see CollapsibleSection. */}
            <CollapsibleSection id="recommended" title="Recommended" count={sectionCounts.recommended}>
                <RecommendedSection navigate={navigate} pollMs={pollMs} onRunsChanged={load} onCountChange={countRecommended} />
            </CollapsibleSection>
            <CollapsibleSection id="jobs" title="Analysis jobs" count={sectionCounts.jobs}>
                <PlannerJobsSection navigate={navigate} pollMs={pollMs} onCountChange={countJobs} />
            </CollapsibleSection>
            <CollapsibleSection id="watch" title="Watch list" count={sectionCounts.watch}>
                <WatchListSection navigate={navigate} pollMs={pollMs} onCountChange={countWatch} />
            </CollapsibleSection>
            <CollapsibleSection id="sweeps" title="Partition sweeps" count={sectionCounts.sweeps}>
                <PartitionSweepsSection navigate={navigate} pollMs={pollMs} onCountChange={countSweeps} />
            </CollapsibleSection>

            {/* Not named in the owner's collapse directive — keeps its own behavior, placed after
                the four collapsed sections so the queue-first order reads cleanly. */}
            <ActionQueueSection navigate={navigate} pollMs={pollMs} />

            <CancelConfirmedShipmentModal
                open={!!cancelTarget}
                onClose={() => setCancelTarget(null)}
                planId={cancelTarget?.inbound_plan_id}
                runLabel={cancelTarget ? `Run #${cancelTarget.id}` : null}
                units={cancelTarget?.total_units}
                asins={cancelTarget?.asins}
                confirmedAt={cancelTarget?.confirmed_at}
                onCancelled={() => {
                    setCancelTarget(null);
                    toast.success('Shipment cancelled.');
                    load();
                }}
            />
        </div>
    );
}
